import axios from "axios";
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { defaultThreadData } from "../../pages/home-page/utilities";
import { ErrorLoggingV2 } from "src/utils/errorHelpers";
import { isStreamActive, msSinceLastFrame } from "src/utils/streamingActivity";
import { trackAnalytics } from "src/pages/helpers";
import { getInhouseAnonymousId, getInhouseAnonymousIdFromCookie } from "src/utils/anonymousId";
import { EVENT_NAMES, EVENT_PARAMETERS, getPageName } from "src/utils/EventConstants";
import { isActivityMessage } from "src/utils/activitySignals";
import { dedupMessages } from "src/utils/messageDedup";
import { resolveQuickActionRefs } from "src/utils/quickActionSets";
import { convertDocumentToGoogleDoc as convertDocumentToGoogleDocService } from "src/services/documents";
import { getShareLinkMessages } from "src/services/threads";
const BASE_ENDPOINT = process.env.REACT_APP_BACKEND_BASE_ENDPOINT;

/**
 * Extract the thread_id from the current shared URL.
 * /chat/shared/{threadId} → threadId
 */
function getSharedThreadIdentifier() {
  const pathParts = window.location.pathname.split("/");
  const sharedIndex = pathParts.indexOf("shared");
  return pathParts[sharedIndex + 1];
}

const SKIP_FOR_LAST_MESSAGE = [
  "quick_actions",
  "quick_action_button_messages",
  "legal_review_message",
  "text_delta",
  "memory_updated",
  // Activity Signals (thinking / web-search) rows are persisted in true position
  // but are never a thread's "last message" preview — they describe an in-progress
  // turn, not its answer.
  "update_loading_message",
  "privileged_chat_started",
  "privileged_chat_ended",
];

const updateThreadLastMessage = (messages) => {
  for (let i = messages.length - 1; i >= 0; i--) {
    const msg = messages[i];
    if (msg && !SKIP_FOR_LAST_MESSAGE.includes(msg.message_type)) {
      return {
        id: msg.id || msg.message_id,
        message_type: msg.message_type,
        message_text: msg.message_text || msg.payload?.text || null,
        created_at: msg.created_at,
        is_user_message: msg.is_user_message || false,
      };
    }
  }
  return null;
};

const insertNewMessages = (messages, action, state) => {
  // De-dup incoming messages by id — the list-messages API can return duplicate IDs under error conditions
  const seen = new Set();
  var newMessages = (action.payload.new_messages || []).filter((m) => {
    const key = m.id || m.message_id;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
  var referenceDoc;

  for (var i = 0; i < newMessages?.length; i++) {
    var message = newMessages[i];

    // A `regenerate_ai_response` marker is the terminal state of a FAILED turn:
    // the backend does NOT emit `enable_messaging` after it. Set the composer's
    // failed/blocked state and stop the awaiting-response timers here so this
    // holds no matter which path inserted the marker (live WS, addMessage, or a
    // list-messages reload).
    if (message?.message_type === "regenerate_ai_response") {
      state.messagingDisabled = true;
      state.threadData.messagingDisabled = true;
      state.awaitingResponse = false;
      state.awaitingResponseStartTime = null;
      state.regenerationHappening = false;
    }

    // Handle quick_actions message type
    if (message?.message_type === "quick_actions") {
      // Transform payload.buttons to quick_action_buttons
      message.quick_action_buttons = message.payload?.buttons || [];
    } else {
      // Handle existing formats
      var quickActionButtons = message?.quick_action_buttons || message?.payload?.quick_action_buttons;
      if (quickActionButtons) {
        message.quick_action_buttons = quickActionButtons;
      }
    }

    if (message?.message_type === "text_delta") {
      const existingIdx = state.threadData.messages.findIndex((m) => m.id === message.id);
      // debugger;
      if (existingIdx >= 0) {
        const existing = state.threadData.messages[existingIdx];
        // debugger;
        state.threadData.messages[existingIdx] = {
          ...existing,
          payload: { text: message.text_delta },
        };
      } else {
        state.threadData.messages.push({
          ...message,
          message_type: "chat",
          payload: { text: message.text_delta },
          // Ensure created_at exists so chronological insertion of later
          // document/activity-signal messages can compare against it.
          created_at: message.created_at || new Date().toISOString(),
        });
        state.streamingMessageId = message.id;
      }
      continue;
    }

    if (message?.message_type === "ai_context_length_exceeded") {
      state.isChatFull = true;
      state.messagingDisabled = true;
      state.awaitingResponse = false;
      continue;
    }

    if (message?.message_type === "document_start") {
      // Stash so the recovery poll can merge it back if the API snapshot
      // doesn't include it yet (same persistence-lag race as "document").
      if (!state.wsDocMessages) state.wsDocMessages = {};
      state.wsDocMessages[message.id] = { ...message };

      // Insert in chronological position based on created_at. Blind "splice before
      // streaming" was wrong — it put document_start before chat1 when chat1 was
      // still streaming, even though document_start is chronologically after chat1.
      const dsInsertIdx = message.created_at
        ? messages.findIndex((m) => m.created_at && new Date(m.created_at) > new Date(message.created_at))
        : -1;
      if (dsInsertIdx >= 0) {
        messages.splice(dsInsertIdx, 0, message);
      } else {
        messages.push(message);
      }
      continue;
    }

    if (message?.message_type === "memory_updated") {
      messages.push(message);
      continue;
    }

    if (message?.message_type === "document") {
      const { doc_id, title, version, markdown_content, is_user_uploaded } = message;
      if (!state.threadData.documents) state.threadData.documents = {};

      // Create — Shape B (separate object, NOT aliased to the message)
      if (!state.threadData.documents[doc_id]) {
        // Check document_payments for an existing unlock entry (e.g. added for paid users
        // before addMessage is dispatched). User-uploaded docs are never locked.
        const hasPaymentUnlock = state.threadData.document_payments?.some(
          (p) => p.doc_id === doc_id && p.has_doc_unlocked,
        );
        state.threadData.documents[doc_id] = {
          meta: {
            title,
            is_user_uploaded,
            latest_version: version,
            is_locked: is_user_uploaded ? false : !hasPaymentUnlock,
          },
          versions: {},
        };
      } else {
        // Update — bump meta.latest_version and keep title fresh
        const meta = state.threadData.documents[doc_id].meta;
        if (title) meta.title = title;
        meta.latest_version = Math.max(meta.latest_version || 1, version);
      }

      // Versions cache — preserves "never overwrite fetched content" across WS reconnects
      const vk = String(version);
      const existingVersion = state.threadData.documents[doc_id].versions[vk];
      if (!existingVersion || existingVersion.fetch_status !== "success") {
        state.threadData.documents[doc_id].versions[vk] = {
          markdown_content: markdown_content || null,
          fetch_status: markdown_content ? "success" : "idle",
        };
      }

      // Sync is_locked onto the message itself for downstream consumers
      message.is_locked = state.threadData.documents[doc_id].meta.is_locked;

      // Stash the WS-received document so the recovery poll (getAskThreadMessages)
      // can merge it back if the API snapshot doesn't include it yet (backend
      // persistence lag). Without this, the document card disappears intermittently
      // when the poll wins the race against backend persistence.
      if (!state.wsDocMessages) state.wsDocMessages = {};
      state.wsDocMessages[message.id] = { ...message };

      // User-uploaded docs are intermediate acks; only AI-generated docs end the response.
      if (!message.is_user_uploaded) {
        state.awaitingResponse = false;
      }

      // Insert in chronological position based on created_at (same as document_start).
      const docInsertIdx = message.created_at
        ? messages.findIndex((m) => m.created_at && new Date(m.created_at) > new Date(message.created_at))
        : -1;
      if (docInsertIdx >= 0) {
        messages.splice(docInsertIdx, 0, { ...message });
      } else {
        messages.push({ ...message });
      }
      continue;
    }

    if (message?.message_type === "locked_document_generated") {
      state.threadData.document = {
        ...state.threadData.document,
        id: message?.payload?.google_doc_id,
        google_doc_id: message?.payload?.google_doc_id,
        isLocked: true,
        doc_title_shorthand: message?.payload?.doc_title,
        doc_title: message?.payload?.doc_title,
        generated_doc_title_shorthand: message?.payload?.doc_title,
        doc_subtitle: message?.payload?.doc_subtitle,
      };
      state.awaitingResponse = false;
      state.messagingDisabled = true;
    }

    if (message?.message_type === "document_generated") {
      state.generateDocsFetchRequiredCount = state.generateDocsFetchRequiredCount + 1;
      // TODO: make a state change so that the frnotned knows that it has to call document-library api

      state.threadData.document = {
        ...state.threadData.document,
        id: message?.payload?.google_doc_id,
        google_doc_id: message?.payload?.google_doc_id,
        isLocked: message?.payload?.is_locked || false,
        doc_title_shorthand: message?.payload?.doc_title,
        doc_title: message?.payload?.doc_title,
        generated_doc_title_shorthand: message?.payload?.doc_title,
      };
      if (!message?.payload?.is_locked) {
        state.unlockingDocument = false;
        state.loadingMessageType = "DEFAULT";
        state.awaitingResponse = false;
      }

    }

    // While the backend is still processing attachments, keep awaitingResponse on so the
    // shimmer renders. Backend never re-emits message_added on processing_complete transition,
    // so the listener for that flip lives in the list-messages poll (see ChatPageComponent).
    if (message?.message_type === "attachment" || message?.message_type === "email_attachment") {
      const processingComplete = message.processing_complete === true || message.payload?.processing_complete === true;
      if (!processingComplete) {
        state.awaitingResponse = true;
      }
    }

    // Activity Signals (design Decision 7): a new-kind `update_loading_message` frame can arrive
    // AFTER the answer has begun streaming. Use chronological insertion (created_at) so each
    // frame lands in the correct position — thinking signals before the answer they precede,
    // tool_executing (e.g. generate_document) after the answer text that announced it.
    // Blind "splice before streaming" was wrong for post-answer signals like tool_executing.
    if (isActivityMessage(message) && state.streamingMessageId && message.created_at) {
      const insertIdx = messages.findIndex(
        (m) => m.created_at && new Date(m.created_at) > new Date(message.created_at),
      );
      if (insertIdx >= 0) {
        messages.splice(insertIdx, 0, message);
      } else {
        messages.push(message);
      }
    } else {
      messages.push(message);
    }

    if (message?.message_type === "free_user_message_limit_reached") {
      state.freeUserLimitReached = true;
    }
  }

  // Dedup by (id, message_type, text) — see src/utils/messageDedup for why the
  // text takes part and why this must stay O(n): it runs on every streamed frame.
  messages = dedupMessages(messages);

  // Update lastMessage — use the full accumulated thread messages (not just the batch)
  // and skip the in-progress streaming message so lastMessage always reflects a complete message
  const allMsgs = state.threadData.messages.filter((m) => m.id !== state.streamingMessageId);

  var formattedLastMsg = updateThreadLastMessage(allMsgs);
  if (formattedLastMsg) {
    state.threadData.lastMessage = formattedLastMsg;
  }

  return { messages: messages, referenceDoc: referenceDoc };
};

export const submitDocumentRating = createAsyncThunk("messageSlice/submitDocumentRating", async (data, thunkAPI) => {
  const accessToken = localStorage.getItem("accessToken2");
  const headers = { Authorization: `Bearer ${accessToken}`, "Content-Type": "application/json" };

  try {
    const url = `${BASE_ENDPOINT}/api/document/${data.thread_id}/add-rating`;
    const response = await axios.post(
      url,
      {
        document_id: data.document_id,
        rating: data.rating,
      },
      { headers },
    );

    return {
      data: response.data,
      rating: data.rating,
      thread_id: data.thread_id,
      document_id: data.document_id,
    };
  } catch (error) {
    return thunkAPI.rejectWithValue({ error: error });
  }
});

const initialState = {
  generateDocsFetchRequiredCount: 0,
  wsErrorCount: 0,
  wsConnectionErrorCount: 0, // New: Track WebSocket connection errors separately
  reconnectionAttemptCount: 0, // Track silent reconnection attempts before showing modal
  showConnectionErrorModal: false,
  userInteractionAttempted: false,
  freeUserLimitReached: false,
  // these are those files which are pending processing, especially the ones coming from landing page
  filesPendingProcessing: [],
  GptThreadNotFoundException: false,
  creationInProgress: false,
  awaitingThreadCreationResponse: false,
  gptContextError: false,
  activeIndex: 0,
  sentences: [],
  fileProcessingStatus: "idle",
  activeSentenceIndex: 0,
  activeSentence: "",
  typedText: "",
  isChatFull: false,
  awaitingCounter: 0,
  newThreadMessage: null,
  changingDraftType: false,
  chatInputMessage: "",
  pendingPromptInjection: null,
  retryCount: 0,
  presignedUrlStatus: "idle",
  presignedUrl: null,
  presignedUrls: {},
  attachments: {},
  attachmentStatuses: {},
  filesToBeProcessed: {},
  attachmentsCleared: {},
  checkingAttachmentAPICallStatus: "idle",
  requestIds: [],
  askToDraftIntent: {},
  askToDraftIntentWSResponse: {},
  threadTitleTop: {},
  documentChangeAction: null,
  regenerationHappening: false,
  wsDocMessages: {},
  unacknowledgedMessages: {},
  showMessagingBlockedModal: false,
  // Document canvas state
  activeDocument: null, // { doc_id: string, version: number }
  documentPanelOpen: false,
  documentPanelOpenedBy: null, // "auto" | "user" — who triggered the panel open
  documentPanelSuppressed: false, // true after user explicitly closes panel; prevents auto-reopen on polling
  activeDiff: null, // { doc_id, from_version, to_version } | null — UI state, cleared on panel close
  newDocArrived: null, // { doc_id, version } | null — set when a doc lands while panel is open on a different doc
  threadData: {
    id: null,
    failedToCreateThread: false,
    duplicateThreadError: false,
    messages: [],
    messagingDisabled: true,
    thread_type: "chat",
    title: null,
    title_source: null,
    is_shared: false,
    legal_review_requested: null,
    documents: {}, // Normalized map: { [doc_id]: { meta: {...}, versions: { "1": { markdown_content, fetch_status } } } }
    diffCache: {}, // { "docId:from:to": { diff_html, fetch_status } } — cleared on thread switch
    document: {
      id: null,
      name: null,
      url: null,
      version: null,
      rating: null,
      placeholders: {
        data: [],
        activePlaceholder: 0,
        dismissedPlaceholders: [],
        answeredPlaceholders: [],
      },
      key_clauses: {},
      assumptions: {},
    },
    lastMessage: null,
    messageFeedback: {},
    documentFeedback: null,
  },
  savedDataInformation: {},
  showSavedDataModal: false,
  showAskToDraftModal: false,
  redirectTo: null,
  draftThreadMessagesFetchStatus: "idle",
  askThreadMessagesFetchStatus: "idle",
  awaitingResponse: false,
  awaitingResponseStartTime: null, // Timestamp when awaitingResponse was set to true
  awaitingResponsePollCount: 0, // Track number of poll attempts for awaiting response
  retryWS: true,
  getThreadStatus: "idle",
  isOwnThreadRedirect: false,
  ownThreadRedirectId: null,
  sharedErrorType: null,
  retryWSState: "idle",
  scroll: false,
  fileUploadprogress: 0,
  isUploading: false,
  fileuploadError: null,
  fileuploadSuccess: false,
  wordCountPopup: false,
  unsupportFilePopup: false,
  unsupportedFileName: null, // Stores the filename(s) of unsupported file(s) for analytics
  unsupportedFileFormat: null, // Stores the file format(s) of unsupported file(s) for analytics
  fileSizeExceededPopup: false,
  blockedThreadWarnPopup: false,
  InvalidThreadOwner: false,
  InvalidThreadId: null,
  InvalidThreadReason: null,
  // Thread ID whose ownership retry has already been spent. Scoped to the
  // thread rather than a boolean, so navigating to a different thread cannot
  // inherit a spent retry — `getAskThreadMessages.pending` clears the Invalid*
  // fields but deliberately not this one, or the retry would re-arm itself and
  // loop (design.md D3: exactly one retry, never a loop).
  ownershipRetryThreadId: null,
  loadingMessageType: "DEFAULT",
  docWordLimitExceeded: false,
  showDocumentLimitPopup: false,
  // Server-provided attachment quota: { attachment_limit, attachment_remaining }.
  // null means "no data yet" (e.g. shared thread or initial load).
  attachmentInfo: null,
  docUploadfailed: false,
  // storage_id of the reference doc whose processing failed — powers the modal's
  // "Try again" (C-4). Cleared when the upload-failed modal is dismissed.
  failedAttachmentStorageId: null,
  fastDraftModeWarning: false,
  callScheduleInRenewalPeriod: null,
  documentRatingStatus: "idle",
  isWsReconnected: false,
  viewDocModal: {
    documentId: null,
    show: false,
    docTitle: "",
  },
  isNewThredCreated: false,
  shouldTriggerAIResponse: false,
  buttonClickTypeForTrigger: null,
  // Id of the user message sent with the most recent `trigger-ai-response` for
  // the current thread. Used to retry a failed turn (regenerate) without having
  // to re-derive the id from history. Reset on thread switch/clear.
  lastTriggeredMessageId: null,
  // New state fields for enhanced WebSocket error handling
  threadLimitReached: false,
  allowRegeneration: false,
  lastErrorMessage: null,
  connectionStatus: "disconnected", // 'connected', 'connecting', 'disconnected', 'error'
  // New state fields for message reliability
  pendingThreadCreation: {
    status: null, // null, 'pending', 'creating', 'created', 'failed'
    threadId: null,
    message: null,
    attachments: null,
    buttonClickType: null,
    retryCount: 0,
    transactionId: null,
    createdAt: null,
    lastAttemptAt: null,
  },
  messageReliability: {
    currentTransaction: null,
    unsentMessages: {}, // {messageId: {id, threadId, message, status, retryCount}}
    failedOperations: [], // [{id, type, error, data, timestamp}]
    recoveryInProgress: false,
  },
  // Phase 4 document unlock states
  unlockingDocument: false,
  showThreadLimitModal: false,
  showUpgradeModal: false,
  documentOperationInProgress: false,
  signupAlertPopup: {
    show: false,
    actionType: "",
  },
  // Hidden payment_confirmation messages with action_taken=false (recovery: Step 11)
  pendingPaymentConfirmations: [],
  streamingMessageId: null,
  // True after the AttachmentsStillProcessing retry loop has exhausted its cap.
  // Drives the "this is taking longer than expected — Refresh" CTA.
  triggerRetryExhausted: false,
  // Shared across SubscribedLimitCard and the ChatBoxComponent banner — both
  // trigger the same summarise-and-continue action for the current thread
  // and must show the same loading/error state regardless of which CTA was clicked.
  isSummarisingToNewChat: false,
  summariseToNewChatError: null,
};

export const getAskThreadMessages = createAsyncThunk(
  "messageSlice/getAskThreadMessages",
  // Accepts either a threadId string (legacy) or `{ threadId, dispatchSource, force }`.
  // `dispatchSource` is a short identifier of the call site (e.g.,
  // "WebSocketMonitor.unack", "ChatBoxComponent.wsReconnect"). It's appended
  // as `?source=...` on the list-messages URL so backend access logs identify
  // which frontend code path triggered the call.
  // `force: true` bypasses the streaming-activity gate (see below).
  async (input, thunkAPI) => {
    const threadId = typeof input === "string" ? input : input?.threadId;
    const dispatchSource = typeof input === "string" ? "legacy-string-arg" : input?.dispatchSource || "unspecified";
    const force = typeof input === "object" && input?.force === true;

    // Centralized streaming-activity gate. If the WS recently delivered a
    // text_delta or heartbeat (within ~5s), the AI is actively streaming
    // and a list-messages REST reconcile would race the live cumulative
    // snapshots. Skip — the next text_delta will deliver the latest state
    // anyway. See `utils/streamingActivity` for rationale.
    //
    // Whitelisted dispatch sources bypass the gate because they're
    // reconciling AFTER a confirmed disruption (recovery poll already
    // saw a quiet window; ws-reconnect paths know the connection just
    // re-established). Anything else is a footgun mid-stream.
    const RECONCILE_AFTER_DISRUPTION = new Set([
      "recovery-poll-success",
      "wsReconnect-recovery-REFRESH",
      "wsReconnect-threadPage",
      "ws-pong-stale-reconnect",
      "ws-backup-reconnect",
    ]);
    if (!force && !RECONCILE_AFTER_DISRUPTION.has(dispatchSource) && isStreamActive()) {
      // Telemetry: leave a breadcrumb so we can grep server logs and
      // confirm the gate is firing in production. Console-only — no
      // dispatch, no Redux churn.
      // eslint-disable-next-line no-console
      console.info(
        `[streamingActivity] suppressed list-messages source=${dispatchSource} thread=${threadId} ` +
        `lastFrame=${msSinceLastFrame()}ms ago`,
      );
      return { skipped: true, reason: "stream-active", dispatchSource };
    }

    var headers;
    var source;
    if (window.location.pathname.includes("/try")) {
      const anonymousId = await getInhouseAnonymousId();
      headers = { "anonymous-user-id": anonymousId };
      source = "unauthenticated";
    } else {
      const accessToken = localStorage.getItem("accessToken2");
      if (!accessToken) {
        return null;
      }
      headers = { Authorization: `Bearer ${accessToken}` };
      source = "authenticated";
    }
    try {
      var url = `${BASE_ENDPOINT}/api/thread/${threadId}/list-messages?source=${encodeURIComponent(dispatchSource)}`;
      var response = await axios.get(url, { headers: headers });
      // Compact responses carry quick-action buttons as refs into
      // `quick_action_sets`; inline them before any reducer sees the messages.
      return { data: resolveQuickActionRefs(response.data), threadId: threadId };
    } catch (error) {
      if ([401, 404, 428].indexOf(error?.response?.status) === -1) {
        const eventName =
          source === "anonymous" ? "failed_to_get_anonymous_thread_messages" : "failed_to_get_thread_messages";
        trackAnalytics(
          eventName,
          {
            error: error?.message || "Unknown error",
            status: error?.response?.status,
            source: source,
            url: error?.config?.url,
            method: error?.config?.method,
          },
          {
            user_id: localStorage.getItem("ajs-user-id") || getInhouseAnonymousIdFromCookie(),
          },
        );
      }
      return thunkAPI.rejectWithValue({ error: error, threadId: threadId, source: source });
    }
  },
);

export const getDocumentContent = createAsyncThunk(
  "messageSlice/getDocumentContent",
  async ({ threadId, docId, version, openShelf = true }, thunkAPI) => {
    const state = thunkAPI.getState().messageSlice;
    const vk = String(version);
    const existing = state.threadData.documents[docId]?.versions[vk];
    if (existing?.fetch_status === "success" && existing.markdown_content) {
      // Already cached — just open the panel, no network call needed
      return { docId, version, markdown_content: existing.markdown_content, cached: true };
    }
    const isShared = thunkAPI.getState().homeslice.isSharedThreadPage;
    const url = isShared
      ? `${BASE_ENDPOINT}/api/thread/${getSharedThreadIdentifier()}/document/get-shared-text/${docId}?version=${version}`
      : `${BASE_ENDPOINT}/api/thread/${threadId}/document/get-text/${docId}?version=${version}`;
    // Try-page (anonymous) requests use the `anonymous-user-id` header, matching
    // the auth pattern in list-messages above. Authenticated routes use the
    // standard Bearer token from localStorage.
    let headers;
    if (window.location.pathname.includes("/try")) {
      const anonymousId = await getInhouseAnonymousId();
      headers = { "anonymous-user-id": anonymousId };
    } else {
      const accessToken = localStorage.getItem("accessToken2");
      headers = { Authorization: `Bearer ${accessToken}` };
    }
    const { data } = await axios.get(url, { headers });
    return { docId: data.doc_id, version: data.version, markdown_content: data.markdown_content, cached: false };
  },
);

export const getDocumentDiff = createAsyncThunk(
  "messageSlice/getDocumentDiff",
  async ({ threadId, docId, fromVersion, toVersion }, thunkAPI) => {
    const key = `${docId}:${fromVersion}:${toVersion}`;
    const cached = thunkAPI.getState().messageSlice.threadData.diffCache?.[key];
    if (cached?.fetch_status === "success" && cached.diff_html) {
      return { docId, fromVersion, toVersion, diff_html: cached.diff_html };
    }
    const isShared = thunkAPI.getState().homeslice.isSharedThreadPage;
    const accessToken = localStorage.getItem("accessToken2");
    const url = isShared
      ? `${BASE_ENDPOINT}/api/thread/${getSharedThreadIdentifier()}/document/get-shared-diff/${docId}?from_version=${fromVersion}&to_version=${toVersion}`
      : `${BASE_ENDPOINT}/api/thread/${threadId}/document/get-diff/${docId}?from_version=${fromVersion}&to_version=${toVersion}`;
    const { data } = await axios.get(url, { headers: { Authorization: `Bearer ${accessToken}` } });
    return {
      docId: data.doc_id,
      fromVersion: data.from_version,
      toVersion: data.to_version,
      diff_html: data.diff_html,
    };
  },
);

export const downloadDocumentFile = createAsyncThunk(
  "messageSlice/downloadDocumentFile",
  async ({ threadId, docId, docTitle, format, version }, thunkAPI) => {
    const isShared = thunkAPI.getState().homeslice.isSharedThreadPage;
    let url = isShared
      ? `${BASE_ENDPOINT}/api/thread/${getSharedThreadIdentifier()}/document/shared-download/${docId}?format=${format}`
      : `${BASE_ENDPOINT}/api/thread/${threadId}/document/download/${docId}?format=${format}`;
    if (version != null) url += `&version=${version}`;
    let headers;
    if (window.location.pathname.includes("/try")) {
      const anonymousId = await getInhouseAnonymousId();
      headers = { "anonymous-user-id": anonymousId };
    } else {
      const accessToken = localStorage.getItem("accessToken2");
      headers = { Authorization: `Bearer ${accessToken}` };
    }
    const response = await axios.get(url, {
      headers,
      responseType: "blob",
    });
    const blobUrl = window.URL.createObjectURL(response.data);
    const link = document.createElement("a");
    link.href = blobUrl;
    link.download = `${docTitle || docId}.${format === "markdown" ? "md" : format}`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(blobUrl);
  },
);

// Download a user-uploaded attachment via presigned S3 URL.
// Unlike downloadDocumentFile (which renders AI-generated nexus documents),
// this hits POST /api/{storage_id}/get-attachment-download-url and fetches the
// original file as-is from S3.
export const downloadUserAttachment = createAsyncThunk(
  "messageSlice/downloadUserAttachment",
  async ({ storageId }) => {
    const accessToken = localStorage.getItem("accessToken2");
    const response = await axios.post(
      `${BASE_ENDPOINT}/api/${storageId}/get-attachment-download-url`,
      null,
      { headers: { Authorization: `Bearer ${accessToken}` } },
    );
    const { url, file_name } = response.data;
    const blobResponse = await axios.get(url, { responseType: "blob" });
    const blobUrl = window.URL.createObjectURL(blobResponse.data);
    const link = document.createElement("a");
    link.href = blobUrl;
    link.download = file_name;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(blobUrl);
  },
);

// Re-trigger backend extraction for a failed attachment (C-4). Gated behind
// `isReprocessEndpointAvailable()` at every call site until the backend-core-v2
// `attachment-error-mapping-and-reprocess` change deploys the endpoint. The thunk
// itself performs no availability check — callers must not dispatch it when the
// endpoint is unavailable.
export const reprocessAttachment = createAsyncThunk(
  "messageSlice/reprocessAttachment",
  async ({ storageId }, thunkAPI) => {
    if (!storageId || storageId === "undefined") {
      return thunkAPI.rejectWithValue({ error: "Invalid storageId", storageId });
    }

    let headers;
    if (window.location.pathname.includes("/try")) {
      const anonymousId = await getInhouseAnonymousId();
      headers = { "anonymous-user-id": anonymousId };
    } else {
      const accessToken = localStorage.getItem("accessToken2");
      headers = { Authorization: `Bearer ${accessToken}` };
    }

    try {
      const response = await axios.post(
        `${BASE_ENDPOINT}/api/attachments/${storageId}/reprocess`,
        null,
        { headers },
      );
      return { data: response.data, storageId };
    } catch (error) {
      return thunkAPI.rejectWithValue({ error, storageId });
    }
  },
);

// Convert the currently-viewed document version into an editable Google Doc and
// return its id. Mirrors downloadDocumentFile's owner-vs-shared + auth-header
// branching, but delegates the actual HTTP call to the typed service. Holds no
// slice state — the component reads google_doc_id via .unwrap() to redirect.
export const convertDocumentToGoogleDoc = createAsyncThunk(
  "messageSlice/convertDocumentToGoogleDoc",
  async ({ threadId, docId, version }, thunkAPI) => {
    const isShared = thunkAPI.getState().homeslice.isSharedThreadPage;
    let headers;
    if (window.location.pathname.includes("/try")) {
      const anonymousId = await getInhouseAnonymousId();
      headers = { "anonymous-user-id": anonymousId };
    } else {
      const accessToken = localStorage.getItem("accessToken2");
      headers = { Authorization: `Bearer ${accessToken}` };
    }
    try {
      const { google_doc_id } = await convertDocumentToGoogleDocService({
        threadId,
        docId,
        version,
        isShared,
        headers,
      });
      return { google_doc_id };
    } catch (error) {
      // Surface the HTTP status to the caller — createAsyncThunk's default error
      // serialization strips `error.response`, so the handler couldn't otherwise
      // distinguish a 403 (locked) from a generic failure.
      return thunkAPI.rejectWithValue({
        status: error?.response?.status,
        message: error?.message,
      });
    }
  },
);

export const getSecuredSharedMessages = createAsyncThunk(
  "messageSlice/getSecuredSharedMessages",
  async (token, thunkAPI) => {
    try {
      const data = resolveQuickActionRefs(await getShareLinkMessages(token));
      return { data, threadId: data.thread_id };
    } catch (error) {
      return thunkAPI.rejectWithValue({ error: error, token: token });
    }
  },
);

export const getPresignedUrl = createAsyncThunk("messageSlice/getPresignedUrl", async (attachmentId, thunkAPI) => {
  var headers;
  if (window.location.pathname.includes("/try")) {
    const anonymousId = await getInhouseAnonymousId();
    headers = { "anonymous-user-id": anonymousId, "Content-Type": "application/json" };
  } else {
    const accessToken = localStorage.getItem("accessToken2");
    headers = { Authorization: `Bearer ${accessToken}`, "Content-Type": "application/json" };
  }
  const state = thunkAPI.getState();
  const attachment = state.messageSlice.attachments[attachmentId];
  const fileName = attachment?.name || attachment?.file?.name;
  var url = `${BASE_ENDPOINT}/api/get-presigned-upload-url`;
  try {
    // const url = `${BASE_ENDPOINT}/api/get-presigned-upload-url`;
    const response = await axios.post(url, { file_name: fileName }, { headers: headers });
    return { data: response.data, attachmentId: attachmentId };
  } catch (error) {
    return thunkAPI.rejectWithValue({ error: error, attachmentId: attachmentId });
  }
});

export const checkAttachmentStatus = createAsyncThunk(
  "messageSlice/checkAttachmentStatus",
  async ({ storageId, attachmentId }, thunkAPI) => {
    var headers;
    if (window.location.pathname.includes("/try")) {
      const anonymousId = await getInhouseAnonymousId();
      headers = { "anonymous-user-id": anonymousId };
    } else {
      const accessToken = localStorage.getItem("accessToken2");
      headers = { Authorization: `Bearer ${accessToken}` };
    }
    var url = `${BASE_ENDPOINT}/api/${storageId}/check-attachment-status`;
    if (storageId === "undefined" || storageId === null || storageId === undefined) {
      // Handle undefined storageId case
      return thunkAPI.rejectWithValue({ error: "Invalid storageId", storageId, attachmentId });
    }

    try {
      const response = await axios.get(url, { headers });
      return { data: response.data, storageId, attachmentId };
    } catch (error) {
      return thunkAPI.rejectWithValue({ error, storageId, attachmentId });
    }
  },
);

export const completeAttachmentUpload = createAsyncThunk(
  "messageSlice/completeAttachmentUpload",
  async ({ storageId, attachmentId }, thunkAPI) => {
    if (!storageId || storageId === "undefined") {
      return thunkAPI.rejectWithValue({ error: "Invalid storageId", storageId, attachmentId });
    }

    let headers;
    if (window.location.pathname.includes("/try")) {
      const anonymousId = await getInhouseAnonymousId();
      headers = { "anonymous-user-id": anonymousId };
    } else {
      const accessToken = localStorage.getItem("accessToken2");
      headers = { Authorization: `Bearer ${accessToken}` };
    }

    try {
      const response = await axios.post(`${BASE_ENDPOINT}/api/attachments/${storageId}/complete`, null, { headers });
      return { data: response.data, storageId, attachmentId };
    } catch (error) {
      return thunkAPI.rejectWithValue({ error, storageId, attachmentId });
    }
  },
);

export const processAttachment = createAsyncThunk("messageSlice/processAttachment", async (data, thunkAPI) => {
  var headers;
  if (window.location.pathname.includes("/try")) {
    const anonymousId = await getInhouseAnonymousId();
    headers = { "anonymous-user-id": anonymousId };
  } else {
    const accessToken = localStorage.getItem("accessToken2");
    headers = { Authorization: `Bearer ${accessToken}` };
  }
  var url = `${BASE_ENDPOINT}/api/${data?.storage_id}/process-attachment`;

  try {
    // const url = `${BASE_ENDPOINT}/api/update-process-file-name/${data?.storage_id}`;
    const response = await axios.patch(url, { file_name: data?.filename }, { headers: headers });
    return { data: response.data };
  } catch (error) {
    return thunkAPI.rejectWithValue({ error: error });
  }
});

export const uploadFile = createAsyncThunk(
  "messageSlice/uploadFile",
  async ({ file, data, url, onUploadProgress }, { rejectWithValue }) => {
    try {
      const formData = new FormData();
      var insertedKeys = [];
      // // console.log(JSON.stringify(formData))
      formData.append("key", data.key);
      formData.append("AWSAccessKeyId", data.AWSAccessKeyId);
      if (data["x-amz-security-token"]) {
        formData.append("x-amz-security-token", data["x-amz-security-token"]);
      }
      formData.append("policy", data.policy);
      formData.append("signature", data.signature);
      formData.append("file", file);
      insertedKeys = ["key", "AWSAccessKeyId", "x-amz-security-token", "policy", "signature"];
      for (var key in data) {
        if (insertedKeys.indexOf(key) === -1) {
          formData.append(key, data[key]);
          insertedKeys.push(key);
        }
      }
      const response = await axios.post(url, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
        onUploadProgress: (progressEvent) => {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          if (onUploadProgress) {
            onUploadProgress(progress); // Callback to update progress
          }
        },
      });

      return response.data; // Return the response data
    } catch (error) {
      return rejectWithValue({
        error: {
          name: error.name,
          message: error.message,
          code: error.code,
          status: error.response?.status,
          responseData: error.response?.data,
        },
      });
    }
  },
);

/**
 * Fulfilled handler for getSecuredSharedMessages, the shared-link thread load.
 * The thunk returns { data, threadId }; this function processes the response
 * and populates state.threadData.
 */
function handleSharedMessagesFulfilled(state, action) {
  let document = null;
  let messages = action?.payload?.data?.messages || [];

  const lockedMessage = messages.find((msg) => msg.message_type === "locked_document_generated");
  const docMessage = messages.find((msg) => msg.message_type === "document_generated");

  if (docMessage) {
    document = {
      id: docMessage?.payload?.google_doc_id,
      name: docMessage?.payload?.doc_title,
      category_id: action.payload.data?.generated_doc_metadata?.document_category_id,
      description: action.payload.data?.generated_doc_metadata?.description,
      reference_doc_storage_id: action.payload.data?.reference_doc_storage_id,
      storage_id: action.payload.data?.storage_id,
      google_doc_id: docMessage?.payload?.google_doc_id,
      google_doc_title: docMessage?.payload?.doc_title,
      isLocked: action.payload.data?.generated_doc_metadata?.is_locked,
      doc_title_shorthand: docMessage?.payload?.doc_title,
    };
  } else if (lockedMessage) {
    document = {
      isLocked: true,
      id: lockedMessage?.payload?.google_doc_id,
      google_doc_id: lockedMessage?.payload?.google_doc_id,
      url: lockedMessage?.payload?.google_doc_id
        ? `https://docs.google.com/document/d/${lockedMessage.payload.google_doc_id}`
        : null,
      google_doc_title: lockedMessage?.payload?.doc_title,
      doc_title: lockedMessage?.payload?.doc_title,
      doc_title_shorthand: lockedMessage?.payload?.doc_title,
      doc_subtitle: lockedMessage?.payload?.doc_subtitle,
    };
  }

  messages = messages.map((msg) => {
    if (msg.quick_action_buttons || msg.payload?.quick_action_buttons) {
      return {
        ...msg,
        quick_action_buttons: msg.quick_action_buttons || msg.payload?.quick_action_buttons,
      };
    }
    return msg;
  });

  let reference_docs = action.payload.data?.attachments_metadata || [];
  if (reference_docs.length === 0) {
    const attachMsg = messages.find((msg) => msg?.payload?.attachments);
    if (attachMsg && Array.isArray(attachMsg.payload.attachments)) {
      reference_docs = [...attachMsg.payload.attachments];
    }
  }

  if (document?.google_doc_id) {
    document.url = `https://docs.google.com/document/d/${document.google_doc_id}`;
  }

  if (document?.google_doc_id && !document.isLocked) {
    const existingDocMessage = messages.find((msg) => msg.message_type === "document_generated");
    if (!existingDocMessage) {
      messages.push({
        id: document.google_doc_id,
        google_doc_id: document.google_doc_id,
        is_user_message: false,
        created_at: new Date(),
        message_type: "document_generated",
        doc_title: document.google_doc_title,
        google_doc_title: document.google_doc_title,
        payload: null,
      });
    }
  }

  const sharedDocMessages = messages.filter((m) => m.message_type === "document");
  const sharedPayments = action.payload?.data?.document_payments || [];
  const sharedPaymentMap = {};
  for (const p of sharedPayments) {
    sharedPaymentMap[p.doc_id] = p;
  }
  const sharedDocuments = {};
  for (const msg of sharedDocMessages) {
    const { doc_id, title, version, is_user_uploaded } = msg;
    if (!sharedDocuments[doc_id]) {
      const isLocked = is_user_uploaded ? false : sharedPaymentMap[doc_id]?.has_doc_unlocked !== true;
      sharedDocuments[doc_id] = {
        meta: { title, is_user_uploaded, latest_version: version, is_locked: isLocked },
        versions: {},
      };
    }
    sharedDocuments[doc_id].versions[String(version)] = { markdown_content: null, fetch_status: "idle" };
    sharedDocuments[doc_id].meta.latest_version = Math.max(sharedDocuments[doc_id].meta.latest_version, version);
  }

  state.threadData = {
    ...state.threadData,
    messages: messages,
    questions: action.payload?.data?.associated_qnas,
    document: document,
    metadata: action.payload.data?.metadata,
    id: action.payload?.threadId,
    isSharedThread: true,
    is_shared: true,
    reference_docs: reference_docs,
    title: action.payload?.data?.thread_title,
    owner_name: action.payload?.data?.owner_name || null,
    reference_doc_storage_id: action.payload.data?.reference_doc_storage_id,
    reference_thread_id: action.payload?.data?.reference_thread_id,
    documents: sharedDocuments,
    document_payments: action.payload?.data?.document_payments || [],
    threads_created_from_summary: action.payload?.data?.threads_created_from_summary || [],
  };

  state.getThreadStatus = "success";
}

const messageSlice = createSlice({
  name: "messageSlice",
  initialState,
  reducers: {
    setFreeUserLimit: (state, action) => {
      state.freeUserLimitReached = action.payload;
    },
    resetThreadData: (state) => {
      state.freeUserLimitReached = false;
      state.isChatFull = false;
      state.lastTriggeredMessageId = null;
      state.regenerationHappening = false;
      state.isSummarisingToNewChat = false;
      state.summariseToNewChatError = null;
      state.attachmentInfo = null; // Clear stale quota so /home falls back to tier-derived limit
      state.wsDocMessages = {}; // Prevent stale doc messages leaking into the next thread
      state.threadData = {
        id: null,
        messages: [],
        messagingDisabled: false,
        thread_type: "chat",
        title: null,
        title_source: null,
        is_shared: false,
        legal_review_requested: null,
        lastMessage: null,
        messageFeedback: {},
        documents: {},
        diffCache: {},
        document: {
          google_doc_id: null,
          id: null,
          name: null,
          isLocked: false,
          url: null,
          version: null,
          rating: null,
          placeholders: {
            data: [],
            activePlaceholder: 0,
            dismissedPlaceholders: [],
            answeredPlaceholders: [],
          },
          key_clauses: {},
          assumptions: {},
        },
      };
    },
    setTypedText: (state, action) => {
      state.typedText = action.payload;
    },
    /**
     * Spend the single ownership retry for a thread. Deliberately NOT cleared
     * by `getAskThreadMessages.pending`, or the retry it triggers would re-arm
     * the guard and loop.
     */
    markOwnershipRetryAttempted: (state, action) => {
      state.ownershipRetryThreadId = action.payload;
    },
    resetInvalidThreadData: (state) => {
      state.InvalidThreadOwner = false;
      state.InvalidThreadId = null;
      state.InvalidThreadReason = null;
      state.ownershipRetryThreadId = null;
    },
    setSentences: (state, action) => {
      state.sentences = action.payload;
    },
    setCreationInProgress: (state, action) => {
      state.creationInProgress = action.payload;
    },
    setAwaitingThreadCreationResponse: (state, action) => {
      state.awaitingThreadCreationResponse = action.payload;
    },
    setResetChatbox: (state, action) => {
      state.resetChatbox = action.payload;
    },
    setActiveIndex: (state, action) => {
      state.activeIndex = action.payload;
    },
    setActiveSentenceIndex: (state, action) => {
      state.activeSentenceIndex = action.payload;
    },
    setShowConnectionErrorModal: (state, action) => {
      state.showConnectionErrorModal = action.payload;
    },
    setUserInteractionAttempted: (state, action) => {
      state.userInteractionAttempted = action.payload;
    },
    setActiveSentence: (state, action) => {
      state.activeSentence = action.payload;
    },
    addRequestIds: (state, action) => {
      state.requestIds.push(action.payload);
    },
    setRetryWSState: (state, action) => {
      state.retryWSState = action.payload;
    },
    setThreadTitleTop: (state, action) => {
      state.threadTitleTop = action.payload;
    },
    setRetryWS: (state, action) => {
      state.retryWS = action.payload;
    },
    setDocumentChangeAction: (state, action) => {
      state.documentChangeAction = action.payload;
    },
    setFailedToCreateThread: (state, action) => {
      state.threadData.failedToCreateThread = action.payload;
    },
    setDuplicateThreadError: (state, action) => {
      state.threadData.duplicateThreadError = action.payload;
    },
    setShowAskToDraftModal: (state, action) => {
      // debugger;
      state.askToDraftIntent = action.payload.intent;
      state.showAskToDraftModal = action.payload.show;
    },
    setAwaitingResponse: (state, action) => {
      state.awaitingResponse = action.payload;
      if (action.payload === true) {
        state.awaitingResponseStartTime = Date.now();
        state.awaitingResponsePollCount = 0; // Reset poll count
      } else {
        state.awaitingResponseStartTime = null;
        state.awaitingResponsePollCount = 0;
      }
    },
    incrementAwaitingResponsePollCount: (state) => {
      state.awaitingResponsePollCount += 1;
    },
    setAwaitingCounter: (state, action) => {
      state.awaitingCounter = action.payload;
    },
    setScroll: (state, action) => {
      state.scroll = action.payload;
    },
    updateRetryCount: (state) => {
      state.retryCount += 1;
    },
    setChatInputMessage: (state, action) => {
      state.chatInputMessage = action.payload;
    },
    setPendingPromptInjection: (state, action) => {
      state.pendingPromptInjection = action.payload;
    },
    resetRedirectTo: (state) => {
      state.redirectTo = null;
    },
    setWsErrorCount: (state, action) => {
      state.wsErrorCount = action.payload;
    },
    setWsConnectionErrorCount: (state, action) => {
      state.wsConnectionErrorCount = action.payload;
    },
    incrementReconnectionAttemptCount: (state) => {
      state.reconnectionAttemptCount += 1;
    },
    resetReconnectionAttemptCount: (state) => {
      state.reconnectionAttemptCount = 0;
    },
    setRedirectTo: (state, action) => {
      state.redirectTo = action.payload;
    },
    setNewThreadMessage: (state, action) => {
      state.newThreadMessage = action.payload;
    },
    setThreadTitle: (state, action) => {
      if (action.payload) {
        state.threadData = { ...state.threadData, title: action.payload };
      }
    },
    setIsNewThreadCreted: (state, action) => {
      state.isNewThredCreated = action.payload;
    },
    setupNewThread: (state, action) => {
      state.InvalidThreadOwner = false;
      state.InvalidThreadId = null;
      state.InvalidThreadReason = null;
      state.ownershipRetryThreadId = null;
      state.creationInProgress = false; // Reset creation in progress when thread is setup

      var thread = Object.assign({}, defaultThreadData);
      if (action.payload.new_messages) {
        var messages = state.threadData.messages;
        // var google_doc_details;
        // var messagingDisabled = action.payload.messaging_disabled;
        var response = insertNewMessages(messages, action, state, false);
        messages = response["messages"];
        thread.messages = messages;
      } else if (action.payload.update_messages) {
      } else if (action.payload.messageId) {
        var messageText = "";
        if (action.payload.threadType === "draft") {
          messageText = action.payload.message;
        } else {
          messageText = state.newThreadMessage;
        }
        var message = {
          thread_id: action.payload.threadId,
          message_id: action.payload.messageId,
          message_text: messageText,
          created_at: new Date(),
          is_user_message: true,
          type: "chat",
          message_type: "chat",
          payload: {
            quick_action_buttons: [],
            text: messageText,
          },
        };

        thread.messages = [message];
      }
      thread.id = action.payload.threadId;
      thread.thread_type = action.payload.threadType;
      state.threadData = thread;
      state.getThreadStatus = "success";

      // Only set redirectTo if this is not part of a pending thread creation flow
      // In two-step flow, redirect should happen only after message is successfully sent
      if (!state.pendingThreadCreation?.status || state.pendingThreadCreation.status === null) {
        state.redirectTo = `/chat/${action.payload.threadId}`;
      }
    },
    updateThreadDataTitle: (state, action) => {
      if (action.payload) {
        if (action.payload.thread_id === state.threadData.id) {
          state.threadData.title = action.payload.title;
          state.threadData.title_source = action.payload.title_source;
        }
      }
    },
    loadThreadTryPage: (state, action) => {
      state.threadData = {
        ...state.threadData,
        messages: action.payload.messages,
        id: action.payload.id,
        questions: action.payload.questions,
        document: action.payload.document,
        reference_docs: action.payload.reference_docs,
      };
    },
    updateReferenceDoc: (state, action) => {
      state.threadData = { ...state.threadData, reference_docs: action.payload };
    },
    setUnacknowledgedMessages: (state, action) => {
      state.unacknowledgedMessages = action.payload;
    },
    showBlockedThreadWarnPopup: (state, action) => {
      state.blockedThreadWarnPopup = action.payload;
    },
    updateUnacknowledgedMessage: (state, action) => {
      const messageId = action.payload;
      if (!state.unacknowledgedMessages[messageId]) {
        return;
      }
      var lastAlertSentAt = Date.now();
      var alertSentCount = (state.unacknowledgedMessages[messageId].alertSentCount || 0) + 1;
      state.unacknowledgedMessages[messageId] = {
        ...state.unacknowledgedMessages[messageId],
        alertSentCount: alertSentCount,
        lastAlertSentAt: lastAlertSentAt,
      };
    },
    setThreadData: (state, action) => {
      state.threadData = action.payload;
    },
    resetFileUploadProgress: (state) => {
      state.fileUploadprogress = 0;
    },
    resetPresignedUrl: (state, action) => {
      state.presignedUrl = null;
    },
    setMessagingDisabled: (state, action) => {
      state.threadData.messagingDisabled = action.payload;
      state.messagingDisabled = action.payload;
    },
    setLastTriggeredMessageId: (state, action) => {
      state.lastTriggeredMessageId = action.payload;
    },
    setTriggerRetryExhausted: (state, action) => {
      state.triggerRetryExhausted = action.payload;
    },
    finalizeStreamingMessage: (state) => {
      if (!state.streamingMessageId) return;
      const idx = state.threadData.messages.findIndex((m) => m.id === state.streamingMessageId);
      if (idx >= 0) {
        state.threadData.lastMessage = {
          id: state.threadData.messages[idx].id,
          message_type: "chat",
          message_text: state.threadData.messages[idx].payload?.text || null,
          created_at: state.threadData.messages[idx].created_at,
          is_user_message: false,
        };
      }
      state.streamingMessageId = null;
    },
    setChangingDraftType: (state, action) => {
      state.changingDraftType = action.payload;
    },
    setThreadType: (state, action) => {
      state.threadData.thread_type = action.payload;
    },
    setTitle: (state, action) => {
      state.threadData.title = action.payload;
    },
    setTitleSource: (state, action) => {
      state.threadData.title_source = action.payload;
    },
    setLegalReviewRequested: (state, action) => {
      state.threadData.legal_review_requested = action.payload;
    },
    setAskToDraftIntentWSResponse: (state, action) => {
      state.askToDraftIntentWSResponse = action.payload;
    },
    setDocument: (state, action) => {
      var documentDetails = {
        google_doc_id: action.payload.generated_google_doc_id,
        id: action.payload.generated_google_doc_id,
        isLocked: action.payload.isLocked,
        url: action.payload.url,
      };
      var messages = state.threadData.messages;
      var messageText = action.payload.summary_text;
      messages.push({
        thread_id: action.payload.threadId,
        message_id: action.payload.messageId,
        message_text: messageText,
        created_at: new Date(),
        source: "ai",
        is_user_message: false,
        quick_action_buttons: [],
        type: "chat",
        message_type: "chat",
        text: messageText,
      });
      state.threadData = { ...state.threadData, document: documentDetails, messages: messages };
      // state.threadData.document = action.payload;
    },
    setMessages: (state, action) => {
      state.threadData.messages = action.payload;
    },
    addInfoMessage: (state, action) => {
      if (state.threadData?.messages?.filter((message) => message.message_type === "doc-unlocked").length === 0) {
        var messages = state.threadData.messages;
        messages.push({
          thread_id: state.threadData?.id,
          message_id: "1234",
          created_at: new Date(),
          source: "ai",
          is_user_message: false,
          quick_action_buttons: [],
          type: "doc-unlocked",
          message_type: "doc-unlocked",
        });
        state.threadData = { ...state.threadData, messages: messages };
      }
    },
    addLegalReviewMessage: (state, action) => {
      if (state.threadData?.messages) {
        var messages = [...state.threadData.messages];
        const newMessage = {
          id: action.payload?.id || `legal-review-${Date.now()}`,
          thread_id: state.threadData?.id,
          is_user_message: false,
          created_at: action.payload?.created_at || new Date().toISOString(),
          message_type: "legal_review_message",
          payload: null,
        };
        messages.push(newMessage);
        state.threadData = { ...state.threadData, messages: messages };
      }
    },
    resetIsChatFull: (state) => {
      state.isChatFull = false;
    },
    updateLoadingMessageType: (state, action) => {
      state.loadingMessageType = action.payload;
    },
    updateThreadMessages: (state, action) => {
      const messages = action.payload;

      const threadMessages = state.threadData?.messages;
      for (let i = 0; i < messages?.length; i++) {
        if (messages[i].message_id) {
          if (threadMessages?.length > 0) {
            const existingMessageIndex = threadMessages.findIndex((message) => message.id === messages[i].message_id);
            if (existingMessageIndex !== -1) {
              Object.keys(messages[i]).forEach((key) => {
                if (key !== "message_id") {
                  if (Array.isArray(threadMessages[existingMessageIndex][key])) {
                    threadMessages[existingMessageIndex][key].push(...messages[i][key]);
                  } else if (
                    threadMessages[existingMessageIndex][key] === null ||
                    typeof threadMessages[existingMessageIndex][key] === "object"
                  ) {
                    threadMessages[existingMessageIndex][key] = {
                      ...threadMessages[existingMessageIndex][key],
                      ...messages[i][key],
                    };
                  } else {
                    threadMessages[existingMessageIndex][key] = messages[i][key];
                  }
                }
              });
            }
          }
        }
      }
      state.threadData = { ...state.threadData, messages: threadMessages };
    },
    addMessage: (state, action) => {
      var messages = state.threadData.messages;
      var response = insertNewMessages(messages, action, state);
      messages = response["messages"];
      // Read AFTER insertNewMessages so a regenerate_ai_response marker (which
      // forces messagingDisabled=true) is preserved. Fall back to the current
      // threadData value rather than clobbering it with `undefined` when the
      // payload omits messaging_disabled.
      var messagingDisabled =
        action.payload.messaging_disabled ?? state.threadData.messagingDisabled;
      var attachments;
      if (action.payload?.new_messages?.filter((message) => message?.payload?.attachments?.length > 0)) {
        attachments = action.payload?.new_messages?.filter((message) => message?.payload?.attachments?.length > 0)?.[0]
          ?.payload?.attachments?.[0];
      }
      if (attachments) {
        state.threadData = {
          ...state.threadData,
          messages: messages,
          reference_docs: attachments,
          messagingDisabled: messagingDisabled,
        };
      } else {
        state.threadData = { ...state.threadData, messages: messages, messagingDisabled: messagingDisabled };
      }
    },
    addFilesToBeProcessed: (state, action) => {
      // insert files that the user just uploaded and check if the file is already being processed or not
      // if the file is already being processed, then do not add it again
      // prepare an object with file_storage_id as key and file details as value
      // action.payload = [
      //   {
      //     file_name: "limited-brochure.pdf",
      //     reference_doc_storage_id: "6866c3a9473d08c08996a8e8",
      //     word_limit_exceeded: false,
      //     status: "processing",
      //     parsed_metadata: null,
      //   },
      // ];
      if (!state.threadData?.id) {
        return;
      }
      var threadAttachments = state.filesToBeProcessed;
      if (!threadAttachments) {
        threadAttachments = {};
      }
      action.payload.forEach((file) => {
        if (!threadAttachments[file.reference_doc_storage_id]) {
          threadAttachments[file.reference_doc_storage_id] = file;
        }
      });
      state.filesToBeProcessed = threadAttachments;
    },
    removeFileFromProcessing: (state, action) => {
      // Remove a single file by reference_doc_storage_id
      // action.payload should be the reference_doc_storage_id string
      if (state.filesToBeProcessed && state.filesToBeProcessed[action.payload]) {
        delete state.filesToBeProcessed[action.payload];
      }
    },
    clearFilesToBeProcessed: (state, action) => {
      // Clear all files from processing
      // Optional: action.payload can be an array of IDs to remove specific files
      if (action.payload && Array.isArray(action.payload)) {
        // Remove specific files
        action.payload.forEach((id) => {
          if (state.filesToBeProcessed[id]) {
            delete state.filesToBeProcessed[id];
          }
        });
      } else {
        // Clear all files
        state.filesToBeProcessed = {};
      }
    },

    setPlaceholders: (state, action) => {
      state.threadData.document.placeholders = action.payload;
    },
    setDocumentIsLocked: (state, action) => {
      state.threadData.document.isLocked = action.payload;
    },
    setPlaceholderDismissed: (state, action) => {
      state.threadData.document.placeholders.dismissedPlaceholders.push(action.payload);
      var lengthPlaceholders = state.threadData.document.placeholders.data.length;
      if (action.payload < lengthPlaceholders - 1) {
        state.threadData.document.placeholders.activePlaceholder = action.payload + 1;
      } else {
        state.threadData.document.placeholders.activePlaceholder = null;
      }
    },
    setPlaceholderAnswered: (state, action) => {
      state.threadData.document.placeholders.answeredPlaceholders[action.payload.index] = action.payload.response;
      var lengthPlaceholders = state.threadData.document.placeholders.data.length;
      if (action.payload < lengthPlaceholders - 1) {
        state.threadData.document.placeholders.activePlaceholder = action.payload + 1;
      } else {
        state.threadData.document.placeholders.activePlaceholder = null;
      }
    },
    //
    setRegenerationHappening: (state, action) => {
      state.regenerationHappening = action.payload;
    },
    setWordCountPopup: (state, action) => {
      state.wordCountPopup = action.payload;
    },
    setUnsupportFilePopup: (state, action) => {
      // Supports both boolean (legacy) and object { show: boolean, filename: string, fileFormat: string }
      if (typeof action.payload === "object" && action.payload !== null) {
        state.unsupportFilePopup = action.payload.show;
        state.unsupportedFileName = action.payload.filename || null;
        state.unsupportedFileFormat = action.payload.fileFormat || null;
      } else {
        state.unsupportFilePopup = action.payload;
        if (!action.payload) {
          state.unsupportedFileName = null; // Clear filename when closing popup
          state.unsupportedFileFormat = null; // Clear file format when closing popup
        }
      }
    },
    setFileSizeExceededPopup: (state, action) => {
      state.fileSizeExceededPopup = action.payload;
    },
    setDocumentTextLengthPopup: (state, action) => {
      state.docWordLimitExceeded = action.payload;
    },
    setShowDocumentLimitPopup: (state, action) => {
      state.showDocumentLimitPopup = action.payload;
    },
    // Update attachment quota from server. null means "no update" — skip it.
    setAttachmentInfo: (state, action) => {
      if (action.payload != null) {
        state.attachmentInfo = action.payload;
      }
    },
    setDocUploadfailed: (state, action) => {
      state.docUploadfailed = action.payload;
      // Dismissing the modal clears the failed storage id so a stale one can't be
      // reprocessed on a later, unrelated failure.
      if (!action.payload) {
        state.failedAttachmentStorageId = null;
      }
    },
    setUnlockingDocument: (state, action) => {
      state.unlockingDocument = action.payload;
    },
    setIsSummarisingToNewChat: (state, action) => {
      state.isSummarisingToNewChat = action.payload;
    },
    setSummariseToNewChatError: (state, action) => {
      state.summariseToNewChatError = action.payload;
    },
    setShowUpgradeModal: (state, action) => {
      state.showUpgradeModal = action.payload;
    },
    setShowThreadLimitModal: (state, action) => {
      state.showThreadLimitModal = action.payload;
    },
    setDocumentOperationInProgress: (state, action) => {
      state.documentOperationInProgress = action.payload;
    },
    setGenerateDocsFetchRequiredCount: (state, action) => {
      state.generateDocsFetchRequiredCount = action.payload;
    },
    addQuickActionButtonsToMessage: (state, action) => {
      const { threadId, quickActionButtons, targetPosition } = action.payload;

      // Only update if this is for the current thread
      if (state.threadData?.id === threadId && state.threadData?.messages) {
        const messages = state.threadData.messages;

        // Find target message based on position
        let targetIndex = -1;
        if (targetPosition === "penultimate" && messages.length >= 2) {
          targetIndex = messages.length - 2; // Second to last message
        } else if (targetPosition === "last" && messages.length >= 1) {
          targetIndex = messages.length - 1; // Last message
        } else if (typeof targetPosition === "number" && targetPosition >= 0 && targetPosition < messages.length) {
          targetIndex = targetPosition; // Specific index
        }

        // Add quick action buttons to the target message
        if (targetIndex >= 0) {
          const updatedMessage = {
            ...messages[targetIndex],
            quick_action_buttons: quickActionButtons,
          };

          // Create new messages array with updated message
          const updatedMessages = [...messages];
          updatedMessages[targetIndex] = updatedMessage;

          state.threadData.messages = updatedMessages;
        }
      }
    },
    showFastDraftModeWarning: (state, action) => {
      state.fastDraftModeWarning = action.payload;
    },
    setCallScheduleInRenewalPeriod: (state, action) => {
      state.callScheduleInRenewalPeriod = action.payload;
    },
    setIsWsReconnected: (state, action) => {
      state.isWsReconnected = action.payload;
    },
    setShowViewDocInModal: (state, action) => {
      state.viewDocModal = {
        documentId: action.payload.documentId,
        show: action.payload.show,
        docTitle: action.payload.docTitle,
        threadId: action.payload.threadId,
      };
    },
    setShouldTriggerAIResponse: (state, action) => {
      state.shouldTriggerAIResponse = action.payload;
    },
    setButtonClickTypeForTrigger: (state, action) => {
      state.buttonClickTypeForTrigger = action.payload;
    },
    // New action creators for enhanced WebSocket error handling
    setThreadLimitReached: (state, action) => {
      state.threadLimitReached = action.payload;
    },
    setAllowRegeneration: (state, action) => {
      state.allowRegeneration = action.payload;
    },
    setLastErrorMessage: (state, action) => {
      state.lastErrorMessage = action.payload;
    },
    setConnectionStatus: (state, action) => {
      state.connectionStatus = action.payload;
    },
    // New action creators for message reliability
    setPendingThreadCreation: (state, action) => {
      state.pendingThreadCreation = {
        ...state.pendingThreadCreation,
        ...action.payload,
        status: "pending",
        createdAt: action.payload.createdAt || Date.now(),
        retryCount: action.payload.retryCount || 0,
      };
    },
    updatePendingThreadStatus: (state, action) => {
      if (state.pendingThreadCreation.status !== null) {
        state.pendingThreadCreation = {
          ...state.pendingThreadCreation,
          ...action.payload,
          lastAttemptAt: Date.now(),
        };
        // Increment retry count on failure
        if (action.payload.status === "failed") {
          state.pendingThreadCreation.retryCount += 1;
        }
      }
    },
    clearPendingThreadCreation: (state) => {
      state.pendingThreadCreation = {
        status: null,
        threadId: null,
        message: null,
        attachments: null,
        buttonClickType: null,
        retryCount: 0,
        transactionId: null,
        createdAt: null,
        lastAttemptAt: null,
      };
    },
    addUnsentMessage: (state, action) => {
      const message = {
        ...action.payload,
        status: action.payload.status || "pending",
        retryCount: action.payload.retryCount || 0,
        timestamp: action.payload.timestamp || Date.now(),
      };
      state.messageReliability.unsentMessages[message.id] = message;
    },
    removeUnsentMessage: (state, action) => {
      const messageId = action.payload;
      if (state.messageReliability.unsentMessages[messageId]) {
        delete state.messageReliability.unsentMessages[messageId];
      }
    },
    updateUnsentMessageStatus: (state, action) => {
      const { id, ...updateData } = action.payload;
      if (state.messageReliability.unsentMessages[id]) {
        state.messageReliability.unsentMessages[id] = {
          ...state.messageReliability.unsentMessages[id],
          ...updateData,
          lastUpdatedAt: Date.now(),
        };
      }
    },
    setMessageTransaction: (state, action) => {
      state.messageReliability.currentTransaction = action.payload;
    },
    updateMessageTransaction: (state, action) => {
      if (state.messageReliability.currentTransaction) {
        state.messageReliability.currentTransaction = {
          ...state.messageReliability.currentTransaction,
          ...action.payload,
          lastUpdatedAt: Date.now(),
        };
      }
    },
    clearMessageTransaction: (state) => {
      state.messageReliability.currentTransaction = null;
    },
    setRecoveryInProgress: (state, action) => {
      state.messageReliability.recoveryInProgress = action.payload;
    },
    addFailedOperation: (state, action) => {
      const operation = {
        ...action.payload,
        timestamp: action.payload.timestamp || Date.now(),
      };
      state.messageReliability.failedOperations.push(operation);
      // Limit to most recent 10 operations
      if (state.messageReliability.failedOperations.length > 10) {
        state.messageReliability.failedOperations = state.messageReliability.failedOperations
          .sort((a, b) => b.timestamp - a.timestamp)
          .slice(0, 10);
      }
    },
    removeFailedOperation: (state, action) => {
      const operationId = action.payload;
      state.messageReliability.failedOperations = state.messageReliability.failedOperations.filter(
        (op) => op.id !== operationId,
      );
    },
    addAttachment: (state, action) => {
      if (!state.attachments) {
        state.attachments = {};
      }
      // Add or update attachment with its ID as the key
      state.attachments[action.payload.id] = action.payload;
    },
    removeAttachment: (state, action) => {
      if (state.attachments && state.attachments[action.payload]) {
        delete state.attachments[action.payload];
      }
      // Also remove from attachment statuses
      if (state.attachmentStatuses[action.payload]) {
        delete state.attachmentStatuses[action.payload];
      }
      // Also remove from presigned URLs
      if (state.presignedUrls[action.payload]) {
        delete state.presignedUrls[action.payload];
      }
    },
    updateAttachmentDetails: (state, action) => {
      const { attachmentId, ...updateData } = action.payload;

      if (state.attachments && state.attachments[attachmentId]) {
        // Much simpler update with object structure
        state.attachments[attachmentId] = {
          ...state.attachments[attachmentId],
          ...updateData,
          // Preserve existing values if not provided in update
          storageId: updateData.storage_id || state.attachments[attachmentId].storageId,
          upload_fields: updateData.upload_fields || state.attachments[attachmentId].upload_fields,
          url: updateData.url || state.attachments[attachmentId].url,
        };

        // // console.log(`Updated attachment ${attachmentId}:`, state.attachments[attachmentId]);
      }
    },
    updateAttachmentStatus: (state, action) => {
      const { attachmentId, status } = action.payload;
      if (!state.attachmentStatuses) {
        state.attachmentStatuses = {};
      }
      state.attachmentStatuses[attachmentId] = {
        ...state.attachmentStatuses[attachmentId],
        ...status,
      };
    },
    markAttachmentPollingTimedOut: (state, action) => {
      const { attachmentId } = action.payload;

      state.attachmentStatuses[attachmentId] = {
        ...state.attachmentStatuses[attachmentId],
        status: "failed",
        percentage: 0,
        failureStage: "processing",
        failureReason: "timeout",
      };

      const processingEntryKey = Object.keys(state.filesToBeProcessed || {}).find(
        (key) =>
          key === attachmentId ||
          state.filesToBeProcessed[key]?.reference_doc_storage_id === attachmentId,
      );
      if (processingEntryKey) {
        delete state.filesToBeProcessed[processingEntryKey];
      }

      state.filesPendingProcessing = (state.filesPendingProcessing || []).filter(
        (id) => id !== attachmentId,
      );

      state.threadData.messages
        ?.filter((message) => message.message_type === "attachment")
        ?.forEach((message) => {
          message.payload?.attachments?.forEach((attachment) => {
            if (attachment.reference_doc_storage_id === attachmentId) {
              attachment.status = "failed";
              attachment.is_ai_parsing = false;
              attachment.processing_timeout = true;
              attachment.percentage = 0;
            }
          });
        });
    },
    clearAllAttachments: (state) => {
      state.clearedAttachments = { ...state.attachments };
      state.attachments = {};
      state.attachmentStatuses = {};
      state.presignedUrls = {};
    },
    removeFailedAttachment: (state, action) => {
      const attachmentId = action.payload;
      if (state.attachments && state.attachments[attachmentId]) {
        delete state.attachments[attachmentId];
      }
      if (state.attachmentStatuses[attachmentId]) {
        delete state.attachmentStatuses[attachmentId];
      }
      if (state.presignedUrls[attachmentId]) {
        delete state.presignedUrls[attachmentId];
      }
    },
    addFeedbackToMessage: (state, data) => {
      const messages = JSON.parse(JSON.stringify(state.threadData.messages));
      var messageIndex = 0;
      for (var i = 0; i < messages.length; i++) {
        if (messages[i].id === data.payload.message_id) {
          messageIndex = i;
          break;
        }
      }
      if (messageIndex > -1) {
        messages[messageIndex].feedback_type = data.payload.feedback_type;
        if (data.payload.negative_feedback_details) {
          messages[messageIndex].negative_feedback_details = data.payload.negative_feedback_details;
        }
      }

      // Update the centralized feedback store in threadData
      const newMessageFeedback = { ...state.threadData.messageFeedback };
      if (data.payload.feedback_type === "none") {
        delete newMessageFeedback[data.payload.message_id];
      } else {
        newMessageFeedback[data.payload.message_id] = data.payload.feedback_type;
      }

      state.threadData.messages = messages;
      state.threadData.messageFeedback = newMessageFeedback;
    },
    setDocumentRating: (state, action) => {
      if (state.threadData.document) {
        state.threadData.document.rating = action.payload.rating;
      }
    },
    setShowMessagingBlockedModal: (state, action) => {
      state.showMessagingBlockedModal = action.payload;
    },
    setGPTContextError: (state, action) => {
      state.gptContextError = action.payload;
    },
    setSignupAlertPopup: (state, action) => {
      state.signupAlertPopup = {
        show: action.payload.show,
        actionType: action.payload.actionType || "",
      };
    },
    handleGPTContextMessages: (state, action) => {
      if (action.payload.new_messages) {
        const messages = state.threadData.messages || [];
        const response = insertNewMessages(messages, action, state);
        state.threadData.messages = response.messages;
      }
    },
    // Document canvas reducers
    addOrUpdateDocument: (state, action) => {
      const { doc_id, title, version, is_user_uploaded, markdown_content } = action.payload;
      const vk = String(version);
      if (!state.threadData.documents[doc_id]) {
        state.threadData.documents[doc_id] = {
          meta: { title, is_user_uploaded, latest_version: version },
          versions: {},
        };
      }
      const doc = state.threadData.documents[doc_id];
      doc.meta.title = title;
      doc.meta.latest_version = Math.max(doc.meta.latest_version, version);
      const existingVk = doc.versions[vk];
      if (!existingVk || existingVk.fetch_status !== "success") {
        doc.versions[vk] = {
          markdown_content: markdown_content || null,
          fetch_status: markdown_content ? "success" : "idle",
        };
      }
    },
    setActiveDocument: (state, action) => {
      state.activeDocument = { doc_id: action.payload.doc_id, version: action.payload.version };
      state.documentPanelOpen = true;
      state.documentPanelOpenedBy = action.payload.openedBy || "auto";
      state.documentPanelSuppressed = false;
      if (state.newDocArrived?.doc_id === action.payload?.doc_id) {
        state.newDocArrived = null;
      }
    },
    closeDocumentPanel: (state) => {
      state.documentPanelOpen = false;
      state.activeDocument = null;
      state.activeDiff = null;
      state.newDocArrived = null;
      state.documentPanelSuppressed = true;
    },
    clearNewDocArrived: (state) => {
      state.newDocArrived = null;
    },
    clearDiff: (state) => {
      state.activeDiff = null;
    },
    cacheDocumentContent: (state, action) => {
      const { doc_id, version, markdown_content } = action.payload;
      const vk = String(version);
      if (state.threadData.documents[doc_id]?.versions[vk]) {
        state.threadData.documents[doc_id].versions[vk].markdown_content = markdown_content;
        state.threadData.documents[doc_id].versions[vk].fetch_status = "success";
      }
    },
    setDocumentVersionFetchStatus: (state, action) => {
      const { doc_id, version, status } = action.payload;
      const vk = String(version);
      if (state.threadData.documents[doc_id]?.versions[vk]) {
        state.threadData.documents[doc_id].versions[vk].fetch_status = status;
      }
    },
    unlockDocumentInCanvas: (state, action) => {
      const { doc_id } = action.payload;
      const doc = state.threadData.documents?.[doc_id];
      if (!doc) return;
      doc.meta.is_locked = false;
      // Reset all cached version content so the panel re-fetches fresh unlocked content
      Object.keys(doc.versions || {}).forEach((vk) => {
        doc.versions[vk] = { markdown_content: null, fetch_status: "idle" };
      });
      // Clear all diff cache entries for this doc
      Object.keys(state.threadData.diffCache || {}).forEach((key) => {
        if (key.startsWith(`${doc_id}:`)) {
          delete state.threadData.diffCache[key];
        }
      });
    },
    addDocumentPayment: (state, action) => {
      const { doc_id } = action.payload;
      if (!state.threadData.document_payments) {
        state.threadData.document_payments = [];
      }
      const existing = state.threadData.document_payments.find((p) => p.doc_id === doc_id);
      if (!existing) {
        state.threadData.document_payments.push({
          doc_id,
          has_doc_unlocked: true,
          has_consultation_purchased: false,
          has_finalisation_purchased: false,
        });
      }
    },
  },
  extraReducers: (builder) => {
    builder

      .addCase(getPresignedUrl.rejected, (state, action) => {
        state.presignedUrlStatus = "failed";
        if (action.payload?.error?.response?.status !== 401) {
          state.presignedUrls[action.payload?.attachmentId] = { status: "failed" };
          ErrorLoggingV2(action.payload.error, "failed_to_get_presigned_url");
        }
      })

      .addCase(getPresignedUrl.pending, (state) => {
        state.presignedUrlStatus = "loading";
      })
      .addCase(getPresignedUrl.fulfilled, (state, action) => {
        state.presignedUrl = action?.payload?.data;
        state.presignedUrlStatus = "success";
        if (action?.payload?.attachmentId) {
          state.presignedUrls[action.payload.attachmentId] = action.payload.data;
        }
      })
      .addCase(checkAttachmentStatus.pending, (state) => {
        state.checkingAttachmentAPICallStatus = "loading";
      })
      .addCase(checkAttachmentStatus.fulfilled, (state, action) => {
        state.checkingAttachmentAPICallStatus = "success";
        const { attachmentId, data } = action.payload;
        const isAiParsing = data.is_ai_parsing === true;
        const processingSuccessful = data.status === "successful" || data.status === "complete";
        const processingTerminal = data.status === "failed" || (processingSuccessful && !isAiParsing);
        const normalizedStatus =
          data.status === "failed"
            ? "failed"
            : isAiParsing
              ? "processing"
              : processingSuccessful
                ? "complete"
                : data.status;
        if (!state.attachmentStatuses) {
          state.attachmentStatuses = {};
        }
        const existingPercentage = state.attachmentStatuses[attachmentId]?.percentage;
        state.attachmentStatuses[attachmentId] = {
          ...state.attachmentStatuses[attachmentId],
          status: normalizedStatus,
          failureStage: data.status === "failed" ? "processing" : undefined,
          metadata: data.metadata,
          percentage:
            processingSuccessful && !isAiParsing
              ? 100
              : data.status === "failed"
                ? 0
                : data.status === "processing"
                  ? existingPercentage !== undefined && existingPercentage >= 98.1
                    ? existingPercentage
                    : 98.1
                  : 98.1,
        };
        if (Object.keys(state.filesToBeProcessed)?.length > 0) {
          var existingDoc = Object.values(state.filesToBeProcessed).find(
            (doc) => doc.reference_doc_storage_id === attachmentId,
          );
          if (existingDoc) {
            existingDoc.status = isAiParsing ? "processing" : processingSuccessful ? "successful" : data.status;
            if (Object.prototype.hasOwnProperty.call(data, "is_ai_parsing")) {
              existingDoc.is_ai_parsing = isAiParsing;
            }
            // Keep the persisted attachment message synchronized with the status endpoint.
            // AttachmentCard resolves from thread messages, so this transition clears its
            // spinner even when the paired document message arrives after the AI response.
            var messages = state.threadData.messages;
            messages
              ?.filter((message) => message.message_type === "attachment")
              ?.forEach((message) => {
                if (message.payload?.attachments) {
                  message.payload.attachments.forEach((attachment) => {
                    if (attachment.reference_doc_storage_id === attachmentId) {
                      attachment.status = processingSuccessful ? "successful" : data.status;
                      if (Object.prototype.hasOwnProperty.call(data, "is_ai_parsing")) {
                        attachment.is_ai_parsing = isAiParsing;
                      }
                      attachment.word_limit_exceeded =
                        data.metadata?.word_limit_exceeded ?? attachment.word_limit_exceeded;
                      attachment.page_limit_exceeded =
                        data.metadata?.page_limit_exceeded ?? attachment.page_limit_exceeded;
                      attachment.parsed_metadata = data.metadata ?? attachment.parsed_metadata;
                      attachment.percentage = processingTerminal ? 100 : attachment.percentage;
                    }
                  });
                }
              });

            if (processingTerminal) {
              // Remove the file only after both extraction and any AI parsing finish.
              var filesToBeProcessedLength = Object.keys(state.filesToBeProcessed).length;
              if (state.filesToBeProcessed[attachmentId]) {
                delete state.filesToBeProcessed[attachmentId];
                if (filesToBeProcessedLength === 1) {
                  state.filesToBeProcessed = {};
                }
              }
              // remove files from filesPendingProcessing
              if (state.filesPendingProcessing.includes(attachmentId)) {
                state.filesPendingProcessing = state.filesPendingProcessing.filter((id) => id !== attachmentId);
              }
            } else if (data.status === "pending") {
              if (!state.filesPendingProcessing.includes(attachmentId)) {
                state.filesPendingProcessing = [...state.filesPendingProcessing, attachmentId];
              }
            } else {
              if (state.filesPendingProcessing.includes(attachmentId)) {
                state.filesPendingProcessing = state.filesPendingProcessing.filter((id) => id !== attachmentId);
              }
            }
          }
        }
      })
      .addCase(checkAttachmentStatus.rejected, (state, action) => {
        state.checkingAttachmentAPICallStatus = "failed";
        if (action.payload?.error?.response?.status !== 401) {
          ErrorLoggingV2(action.payload.error, "failed_to_check_attachment_status");
        }
      })
      .addCase(getAskThreadMessages.pending, (state) => {
        state.getThreadStatus = "loading";
        state.GptThreadNotFoundException = false;
        state.InvalidThreadOwner = false;
        state.InvalidThreadId = null;
        state.InvalidThreadReason = null;
      })
      .addCase(getAskThreadMessages.fulfilled, (state, action) => {
        // The thunk now short-circuits with { skipped: true, ... } when the
        // streaming-activity gate fires (active WS stream, non-recovery
        // dispatchSource). Treat that as a no-op — leave state untouched.
        if (action.payload?.skipped) {
          state.getThreadStatus = "success"; // not "loading", since the thunk completed
          return;
        }
        // Reset suppression flag when navigating to a different thread so the new thread's
        // document panel can auto-open on load.
        if (state.threadData.id && state.threadData.id !== action.payload?.data?.thread_id) {
          state.documentPanelSuppressed = false;
          state.wsDocMessages = {};
          state.attachmentInfo = null; // Reset quota when switching threads
        }
        state.GptThreadNotFoundException = false;
        state.InvalidThreadOwner = false;
        state.InvalidThreadId = null;
        state.InvalidThreadReason = null;
        var document = {};
        let documentGeneratedMessage = action?.payload?.data?.messages?.find(
          (msg) => msg.message_type === "document_generated",
        );
        if (documentGeneratedMessage) {
          document = {
            id: documentGeneratedMessage?.payload?.google_doc_id,
            name: documentGeneratedMessage?.payload?.doc_title,
            category_id: action.payload.data?.generated_doc_metadata?.document_category_id,
            description: action.payload.data?.generated_doc_metadata?.description,
            reference_doc_storage_id: action.payload.data?.reference_doc_storage_id,
            storage_id: action.payload.data?.storage_id,
            google_doc_id: documentGeneratedMessage?.payload?.google_doc_id,
            google_doc_title: documentGeneratedMessage?.payload?.doc_title,
            isLocked: action.payload.data?.generated_doc_metadata?.is_locked,
            doc_title_shorthand: documentGeneratedMessage?.payload?.doc_title,
          };
        }
        // Extract hidden payment_confirmation messages with action_taken=false for recovery (Step 11)
        const pendingPaymentConfirmations = (action?.payload?.data?.messages || []).filter(
          (msg) =>
            msg.message_type === "payment_confirmation" && msg.visible === false && msg.payload?.action_taken === false,
        );
        if (pendingPaymentConfirmations.length > 0) {
          state.pendingPaymentConfirmations = pendingPaymentConfirmations;
        }

        var messages =
          action?.payload?.data?.messages
            ?.filter((message) => message.message_type !== "payment_confirmation")
            ?.map((message) => {
              // Handle new quick_actions message type
              if (message.message_type === "quick_actions" && message.payload?.buttons) {
                return {
                  ...message,
                  quick_action_buttons: message.payload.buttons,
                };
              }
              // Preserve quick action buttons if they exist in other formats
              if (message.quick_action_buttons || message.payload?.quick_action_buttons) {
                return {
                  ...message,
                  quick_action_buttons: message.quick_action_buttons || message.payload?.quick_action_buttons,
                };
              }
              return message;
            }) || [];

        if (
          document?.google_doc_id &&
          messages?.filter((message) => message.message_type === "document_generated").length === 0
        ) {
          messages.push({
            id: document.google_doc_id,
            google_doc_id: document.google_doc_id,
            is_user_message: false,
            created_at: new Date(),
            message_type: "document_generated",
            doc_title: document.google_doc_title,
            google_doc_title: document.google_doc_title,
            payload: null,
          });
        }
        var lockedmessages = messages.find((msg) => msg.message_type === "locked_document_generated");
        if (lockedmessages && !documentGeneratedMessage) {
          // let message = messages.find((msg) => msg.message_type === "locked_document_generated");
          document["isLocked"] = true;
          document["id"] = lockedmessages?.payload?.google_doc_id;
          document["google_doc_id"] = lockedmessages?.payload?.google_doc_id;
          document["url"] = lockedmessages?.payload?.google_doc_id
            ? "https://docs.google.com/document/d/" + lockedmessages?.payload?.google_doc_id
            : null;
          document["google_doc_title"] = lockedmessages?.payload?.doc_title;
          document["doc_title"] = lockedmessages?.payload?.doc_title;
          document["doc_title_shorthand"] = lockedmessages?.payload?.doc_title;
          document["doc_subtitle"] = lockedmessages?.payload?.doc_subtitle;
        }
        var reference_docs = [];
        if (action?.payload?.data?.attachments_metadata?.length > 0) {
          reference_docs = action.payload.data.attachments_metadata;
        } else {
          if (
            messages?.filter(
              (message) => message?.payload?.attachments !== null && message?.payload?.attachments !== undefined,
            ).length > 0
          ) {
            var attachments = messages?.filter(
              (message) =>
                message?.payload &&
                message?.payload?.attachments !== null &&
                message?.payload?.attachments !== undefined,
            )?.[0]?.payload?.attachments;
            if (Array.isArray(attachments)) {
              attachments?.forEach((attachment) => {
                if (reference_docs.indexOf(attachment) === -1) {
                  reference_docs.push(attachment);
                }
              });
            }
          }
        }
        const ratings = action.payload?.data?.document_rating;
        const allDocumentFeedback = ratings
          ? Object.entries(ratings).map(([key, value]) => ({
            id: key,
            rating: value,
          }))
          : null;

        // Merge WS-received document/document_start messages that the API snapshot
        // doesn't include yet. wsDocMessages is populated by insertNewMessages whenever
        // a "document" or "document_start" WS event arrives. It survives list-messages
        // overwrites (unlike state.threadData.messages) so the merge works regardless
        // of whether WS arrives before or after list-messages.
        const wsDocMsgs = Object.values(state.wsDocMessages || {});
        if (wsDocMsgs.length > 0) {
          const apiMsgIds = new Set(messages.map((m) => m.id));
          const pendingWSDocs = wsDocMsgs.filter((m) => !apiMsgIds.has(m.id));
          if (pendingWSDocs.length > 0) {
            messages = [...messages, ...pendingWSDocs];
          }
          // Clean up entries the API now includes — no need to keep them around.
          for (const m of wsDocMsgs) {
            if (apiMsgIds.has(m.id)) {
              delete state.wsDocMessages[m.id];
            }
          }
        }

        // Preserve in-flight streaming message if the API snapshot doesn't include it yet.
        // Why: the recovery poll can win a race against the live WS stream and replace
        // threadData.messages with a snapshot taken before USER_REPLY_TEXT persisted —
        // wiping the partially-streamed assistant text the user was watching.
        if (state.streamingMessageId) {
          const streaming = state.threadData?.messages?.find((m) => m.id === state.streamingMessageId);
          if (streaming && !messages.some((m) => m.id === streaming.id)) {
            // The API may already contain a status that arrived after this
            // live text frame. Keep that ordering so the drafting card stays
            // at the tail instead of being replaced by the text dots loader.
            const draftingStatusIndex = messages.findIndex(
              (m) =>
                m.trigger_id === streaming.trigger_id &&
                ((m.message_type === "update_loading_message" && m.payload?.doc_generation_payload) ||
                  m.message_type === "document_start"),
            );
            if (draftingStatusIndex >= 0) {
              messages.splice(draftingStatusIndex, 0, streaming);
            } else {
              messages.push(streaming);
            }
          }
        }

        // Extract attachment_info from list-messages response (null on shared threads).
        const responseAttachmentInfo = action.payload?.data?.attachment_info;
        if (responseAttachmentInfo != null) {
          state.attachmentInfo = responseAttachmentInfo;
        }

        const lastAllowedMessage = updateThreadLastMessage(messages);
        state.threadData = {
          ...state.threadData,
          title: action.payload?.data?.thread_title,
          messages: messages,
          questions: action.payload?.data?.associated_qnas,
          document: document,
          metadata: action.payload?.data?.metadata,
          id: action.payload?.data?.thread_id,
          messagingDisabled: action.payload?.data?.messaging_disabled,
          cached_party_details: action.payload?.data?.generated_doc_metadata?.cached_party_details,
          party_id: action.payload?.data?.generated_doc_metadata?.party_id,
          reference_thread_id: action.payload?.data?.reference_thread_id,
          reference_docs: reference_docs,
          messageFeedback: action.payload?.data?.message_feedback || {},
          documentFeedback: allDocumentFeedback,
          lastMessage: lastAllowedMessage,
          hasDocUnlocked: action.payload?.data?.has_doc_unlocked || false,
          hasConsultationPurchased: action.payload?.data?.has_consultation_purchased || false,
          document_payments: action.payload?.data?.document_payments || [],
          threads_created_from_summary: action.payload?.data?.threads_created_from_summary || [],
        };
        state.getThreadStatus = "success";

        // Re-populate filesToBeProcessed from any attachment messages that still
        // have non-terminal statuses. This fixes a race where the poller already
        // removed a file (check-status returned "successful"), but list-messages
        // overwrites the payload with stale server data (still "processing").
        // Without this, no further polling happens and the spinner sticks forever.
        const attachmentMessages = messages.filter((m) => m.message_type === "attachment");
        for (const msg of attachmentMessages) {
          const atts = msg.payload?.attachments;
          if (!Array.isArray(atts)) continue;
          for (const att of atts) {
            const id = att.reference_doc_storage_id;
            if (!id) continue;
            const isNonTerminal =
              att.status === "processing" || att.status === "pending" || att.is_ai_parsing === true;
            if (isNonTerminal && !state.filesToBeProcessed[id]) {
              state.filesToBeProcessed[id] = att;
            }
          }
        }

        // Rebuild the normalized documents map from document messages in history.
        // Merges with existing state so cached content is not discarded on every
        // list-messages poll. Cached versions are preserved as-is; new versions
        // (not yet in state) are registered as idle for on-demand fetch.
        // Lock status is derived from document_payments (server-authoritative).
        // unlockDocumentInCanvas still handles optimistic UI after payment.
        const docMessages = messages.filter((m) => m.message_type === "document");
        const existingDocuments = state.threadData.documents || {};

        // Build a lookup from document_payments for authoritative per-doc lock status.
        // A doc is unlocked only if the server confirms has_doc_unlocked=true.
        const documentPayments = action.payload?.data?.document_payments || [];
        const paymentMap = {};
        for (const p of documentPayments) {
          paymentMap[p.doc_id] = p;
        }

        const nextDocuments = {};
        for (const msg of docMessages) {
          const { doc_id, title, version, is_user_uploaded } = msg;
          const vk = String(version);
          const existingDoc = existingDocuments[doc_id];
          if (!nextDocuments[doc_id]) {
            const isLocked = is_user_uploaded ? false : paymentMap[doc_id]?.has_doc_unlocked !== true;
            // If the document was previously locked and is now unlocked, clear
            // cached version content so the panel re-fetches full markdown
            // instead of showing the old truncated/locked preview.
            const wasLocked = existingDoc?.meta?.is_locked;
            const transitionedToUnlocked = wasLocked && !isLocked;
            let versions = existingDoc ? { ...existingDoc.versions } : {};
            if (transitionedToUnlocked) {
              const cleared = {};
              for (const vk2 of Object.keys(versions)) {
                cleared[vk2] = { markdown_content: null, fetch_status: "idle" };
              }
              versions = cleared;
            }
            nextDocuments[doc_id] = {
              meta: {
                title,
                is_user_uploaded,
                latest_version: version,
                is_locked: isLocked,
              },
              versions,
            };
          } else {
            nextDocuments[doc_id].meta.latest_version = Math.max(nextDocuments[doc_id].meta.latest_version, version);
          }
          if (!nextDocuments[doc_id].versions[vk]) {
            nextDocuments[doc_id].versions[vk] = { markdown_content: null, fetch_status: "idle" };
          }
        }
        state.threadData.documents = nextDocuments;

        // Restore document panel on page refresh: if the panel is not already open and the user
        // has not explicitly closed it, auto-open the latest eligible document.
        if (!state.documentPanelOpen && !state.documentPanelSuppressed) {
          const lastEligibleMsg = docMessages
            .slice()
            .reverse()
            .find((m) => nextDocuments[m.doc_id] && !nextDocuments[m.doc_id].meta.is_locked && !m.is_user_uploaded);
          if (lastEligibleMsg) {
            const latestVersion = nextDocuments[lastEligibleMsg.doc_id].meta.latest_version;
            state.activeDocument = { doc_id: lastEligibleMsg.doc_id, version: latestVersion };
            state.documentPanelOpen = true;
            state.documentPanelOpenedBy = "auto";
          }
        }

        // Sync is_locked on individual document messages from the authoritative computed value
        state.threadData.messages.forEach((msg) => {
          if (msg.message_type === "document" && nextDocuments[msg.doc_id]) {
            msg.is_locked = nextDocuments[msg.doc_id].meta.is_locked;
          }
        });

        if (action.payload?.data?.messaging_disabled) {
          state.messagingDisabled = true;
        }

        var isChatFull =
          messages.filter(
            (message) => message.message_type === "ai_context_length_exceeded",
            // || message.message_type === "free_user_message_limit_exceeded"
          ).length > 0;
        if (isChatFull) {
          state.isChatFull = true;
        }
        if (action.payload?.data?.messaging_disabled && !isChatFull) {
          state.messagingDisabled = true;
        }
        if (action.payload?.data?.messaging_disabled && messages.length < 3) {
          state.messagingDisabled = true;
          state.awaitingResponse = true;
        }

        if (messages.length > 0) {
          const lastMessage = messages[messages.length - 1];
          const secondLastMessage = messages.length > 1 ? messages[messages.length - 2] : null;

          const hasDocumentGenerated =
            lastMessage?.message_type === "document_generated" ||
            secondLastMessage?.message_type === "document_generated";
          if (hasDocumentGenerated) {
            state.messagingDisabled = false;
            state.threadData.messagingDisabled = false;
            state.awaitingResponse = false;
          }
        }

        // Reset awaiting states when messaging is not disabled
        if (!action.payload?.data?.messaging_disabled) {
          state.messagingDisabled = false;
          // If the last message is from the user, the AI is still processing —
          // show the loading shimmer so it doesn't look like the AI is stuck.
          const lastMsg = messages?.[messages.length - 1];
          state.awaitingResponse = !!lastMsg?.is_user_message;
        }
      })
      .addCase(getAskThreadMessages.rejected, (state, action) => {
        state.getThreadStatus = "failed";
        var errorDetail = action.payload?.error?.response?.data?.detail;
        if (
          errorDetail === "User is not the owner of the thread" ||
          errorDetail === "User for given thread messages is not anonymous"
        ) {
          state.InvalidThreadOwner = true;
          state.InvalidThreadId = action.payload?.threadId;
          if (errorDetail === "User is not the owner of the thread") {
            state.InvalidThreadReason = "invalid_thread_owner";
          } else if (errorDetail === "User for given thread messages is not anonymous") {
            state.InvalidThreadReason = "invalid_thread_owner_or_anonymous_user_for_thread_messages";
          } else {
            state.InvalidThreadReason = "unauthorized_thread_access";
          }

          // TODO: separate error logging for this case
          ErrorLoggingV2(action.payload.error, "invalid_thread_owner_or_anonymous_user_for_thread_messages");
          return;
        }

        if (action.payload?.error?.response?.status === 428) {
          state.GptThreadNotFoundException = true;
          state.InvalidThreadReason = "gpt_thread_not_found";
          return;
        }
        if (action.payload?.error?.response?.status === 400) {
          // gpt -> try -> signup -> logout -> gpt -> same thread -> try page.
          // in this case, the thread has been mapped to a user, so its no longer anon
          // TODO: separate error logging for this case
          if (action.payload?.error?.response?.data?.detail === "User for given thread messages is not anonymous") {
            state.InvalidThread = true;
            state.InvalidThreadMessage = "";
          }
        }

        if (action.payload?.error?.response?.status === 422) {
          state.threadData.failedToCreateThread = true;
        }
      })
      .addCase(getSecuredSharedMessages.pending, (state) => {
        state.getThreadStatus = "loading";
      })
      .addCase(getSecuredSharedMessages.fulfilled, handleSharedMessagesFulfilled)
      .addCase(getSecuredSharedMessages.rejected, (state, action) => {
        const status = action.payload?.error?.response?.status;
        const detail = action.payload?.error?.response?.data?.detail;
        const errType = typeof detail === "object" ? detail?.error_type : null;

        if (errType === "owner_redirect") {
          state.isOwnThreadRedirect = true;
          state.ownThreadRedirectId = typeof detail === "object" ? detail?.thread_id : null;
          state.getThreadStatus = "loading";
        } else {
          state.getThreadStatus = "failed";
          state.sharedErrorType = errType || "unknown";
          if (status !== 404) {
            ErrorLoggingV2(action?.payload?.error, "failed_to_get_secured_shared_messages");
          }
        }
      })

      .addCase(uploadFile.pending, (state) => {
        state.isUploading = true;
        // state.fileUploadprogress = 0;
        state.fileuploadSuccess = false;
        state.fileuploadError = null;
        state.fileProcessingStatus = "idle";
        state.fileuploadError = null;
        state.docWordLimitExceeded = false;
      })
      .addCase(uploadFile.fulfilled, (state) => {
        state.isUploading = false;
        state.fileuploadSuccess = true;
        state.fileuploadError = null;
      })
      .addCase(uploadFile.rejected, (state, action) => {
        state.isUploading = false;
        state.fileuploadSuccess = false;
        state.fileuploadError = action.payload;
        if (action.payload?.error?.response?.status !== 401) {
          ErrorLoggingV2(action?.payload?.error, "failed_to_upload_file");
        }
      })

      .addCase(processAttachment.pending, (state) => {
        state.fileProcessingStatus = "loading";
        state.docWordLimitExceeded = false;
      })
      .addCase(processAttachment.fulfilled, (state, action) => {
        state.fileProcessingStatus = "success";
        state.docWordLimitExceeded = action.payload?.data?.word_limit_exceeded;
      })
      .addCase(processAttachment.rejected, (state, action) => {
        state.fileProcessingStatus = "failed";
        state.docUploadfailed = true;
        state.failedAttachmentStorageId = action.meta?.arg?.storage_id || null;
        if (action.payload?.error?.response?.status !== 401) {
          ErrorLoggingV2(action?.payload?.error, "update_uploaded_file_name_error");
        }
      })

      // Document rating handlers
      .addCase(submitDocumentRating.pending, (state) => {
        state.documentRatingStatus = "loading";
      })
      .addCase(submitDocumentRating.fulfilled, (state, action) => {
        state.documentRatingStatus = "success";
        if (state.threadData.document) {
          state.threadData.document.rating = action.payload.rating;
        }
      })
      .addCase(submitDocumentRating.rejected, (state, action) => {
        state.documentRatingStatus = "failed";
        if (action.payload?.error?.response?.status !== 401) {
          ErrorLoggingV2(action?.payload?.error, "submit_document_rating_error");
        }
      })

      // Document canvas content fetch handlers
      .addCase(getDocumentContent.pending, (state, action) => {
        const { docId, version } = action.meta.arg;
        const vk = String(version);
        if (state.threadData.documents[docId]?.versions[vk]) {
          state.threadData.documents[docId].versions[vk].fetch_status = "loading";
        }
      })
      .addCase(getDocumentContent.fulfilled, (state, action) => {
        const { docId, version, markdown_content } = action.payload;
        const vk = String(version);
        if (state.threadData.documents[docId]?.versions[vk]) {
          state.threadData.documents[docId].versions[vk].markdown_content = markdown_content;
          state.threadData.documents[docId].versions[vk].fetch_status = markdown_content ? "success" : "empty";
        }
        // v0 is the user-uploaded original; never open the panel for it, even if a stale dispatch slips through.
        if (action.meta.arg.openShelf !== false && Number(version) !== 0) {
          state.activeDocument = { doc_id: docId, version };
          state.documentPanelOpen = true;
          // INH-5557 opened_by: callers that open the panel on a user click pass openedBy: "user"
          // (document card, Docs in chat drawer); everything else is an automatic open.
          state.documentPanelOpenedBy = action.meta.arg.openedBy || "auto";
        }
        if (state.newDocArrived?.doc_id === docId) {
          state.newDocArrived = null;
        }
      })
      .addCase(getDocumentContent.rejected, (state, action) => {
        const { docId, version } = action.meta.arg;
        const vk = String(version);
        if (state.threadData.documents[docId]?.versions[vk]) {
          state.threadData.documents[docId].versions[vk].fetch_status = "failed";
        }
      })

      .addCase(getDocumentDiff.pending, (state, action) => {
        const { docId, fromVersion, toVersion } = action.meta.arg;
        if (!state.threadData.diffCache) state.threadData.diffCache = {};
        state.threadData.diffCache[`${docId}:${fromVersion}:${toVersion}`] = {
          diff_html: null,
          fetch_status: "loading",
        };
      })
      .addCase(getDocumentDiff.fulfilled, (state, action) => {
        const { docId, fromVersion, toVersion, diff_html } = action.payload;
        if (!state.threadData.diffCache) state.threadData.diffCache = {};
        state.threadData.diffCache[`${docId}:${fromVersion}:${toVersion}`] = { diff_html, fetch_status: "success" };
        state.activeDiff = { doc_id: docId, from_version: fromVersion, to_version: toVersion };
      })
      .addCase(getDocumentDiff.rejected, (state, action) => {
        const { docId, fromVersion, toVersion } = action.meta.arg;
        if (!state.threadData.diffCache) state.threadData.diffCache = {};
        state.threadData.diffCache[`${docId}:${fromVersion}:${toVersion}`] = {
          diff_html: null,
          fetch_status: "failed",
        };
      });
  },
});

export const {
  setThreadData,
  setMessagingDisabled,
  setLastTriggeredMessageId,
  setTriggerRetryExhausted,
  setThreadType,
  setTitle,
  setTitleSource,
  setLegalReviewRequested,
  setDocument,
  setMessages,
  addMessage,
  setPlaceholders,
  setPlaceholderDismissed,
  setPlaceholderAnswered,
  setKeyClauses,
  setAssumptions,
  setKeyClauseDismissed,
  setKeyClauseAnswered,
  setNewThreadMessage,
  setupNewThread,
  resetRedirectTo,
  setRedirectTo,
  setChatInputMessage,
  setPendingPromptInjection,
  updateRetryCount,
  setAwaitingResponse,
  incrementAwaitingResponsePollCount,
  setShowAskToDraftModal,
  setRetryWS,
  setRetryWSState,
  setThreadTitle,
  setThreadTitleTop, // this is used specifically for the top navbar
  setScroll,
  resetThreadData,
  resetPresignedUrl,
  resetFileUploadProgress,
  addRequestIds,
  setAskToDraftIntentWSResponse,
  setDocumentChangeAction,
  setFailedToCreateThread,
  setDuplicateThreadError,
  setRegenerationHappening,
  setWordCountPopup,
  setChangingDraftType,
  loadThreadTryPage,
  setAwaitingCounter,
  updateReferenceDoc,
  addInfoMessage,
  addLegalReviewMessage,
  resetIsChatFull,
  setTypedText,
  setSentences,
  setActiveIndex,
  setActiveSentenceIndex,
  setCreationInProgress,
  setAwaitingThreadCreationResponse,
  setUnsupportFilePopup,
  setFileSizeExceededPopup,
  setDocumentTextLengthPopup,
  resetInvalidThreadData,
  markOwnershipRetryAttempted,
  setDocumentIsLocked,
  updateLoadingMessageType,
  setShowDocumentLimitPopup,
  setAttachmentInfo,
  setDocUploadfailed,
  setUnlockingDocument,
  setIsSummarisingToNewChat,
  setSummariseToNewChatError,
  setShowUpgradeModal,
  setShowThreadLimitModal,
  setDocumentOperationInProgress,
  setGenerateDocsFetchRequiredCount,
  addQuickActionButtonsToMessage,
  setUnacknowledgedMessages,
  updateUnacknowledgedMessage,
  setResetChatbox,
  showFastDraftModeWarning,
  setCallScheduleInRenewalPeriod,
  setShowConnectionErrorModal,
  setUserInteractionAttempted,
  setIsWsReconnected,
  setShowViewDocInModal,
  setShouldTriggerAIResponse,
  setButtonClickTypeForTrigger,
  updateThreadMessages,
  updateThreadDataTitle,
  addAttachment,
  removeAttachment,
  updateAttachmentStatus,
  markAttachmentPollingTimedOut,
  clearAllAttachments,
  removeFailedAttachment,
  updateAttachmentDetails,
  addFilesToBeProcessed,
  removeFileFromProcessing,
  clearFilesToBeProcessed,
  addFeedbackToMessage,
  setDocumentRating,
  setFreeUserLimit,
  // New action creators for enhanced WebSocket error handling
  setThreadLimitReached,
  setAllowRegeneration,
  setGPTContextError,
  handleGPTContextMessages,
  setLastErrorMessage,
  setConnectionStatus,
  // New action creators for message reliability
  setPendingThreadCreation,
  updatePendingThreadStatus,
  clearPendingThreadCreation,
  addUnsentMessage,
  removeUnsentMessage,
  updateUnsentMessageStatus,
  setMessageTransaction,
  updateMessageTransaction,
  clearMessageTransaction,
  setRecoveryInProgress,
  addFailedOperation,
  removeFailedOperation,
  setIsNewThreadCreted,
  setWsErrorCount,
  setWsConnectionErrorCount,
  incrementReconnectionAttemptCount,
  resetReconnectionAttemptCount,
  setShowMessagingBlockedModal,
  showBlockedThreadWarnPopup,
  setSignupAlertPopup,
  finalizeStreamingMessage,
  // Document canvas actions
  addOrUpdateDocument,
  setActiveDocument,
  closeDocumentPanel,
  clearNewDocArrived,
  cacheDocumentContent,
  setDocumentVersionFetchStatus,
  clearDiff,
  unlockDocumentInCanvas,
  addDocumentPayment,
} = messageSlice.actions;
export default messageSlice.reducer;
